import React, { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import UploadZone from './components/UploadZone';
import Editor from './components/Editor';
import ResultViewer from './components/ResultViewer';
import SendToTourModal from './components/SendToTourModal';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import BuyCreditsModal from './components/BuyCreditsModal';
import { LowCreditsBanner } from '@rehub/buy-credits';
import AdminPanel from './components/AdminPanel';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';
import EmbedPage from './components/EmbedPage';
import LandingPage from './components/LandingPage';
import CommerceOrdersList from './components/CommerceOrdersList';
import CommerceOrderDetail from './components/CommerceOrderDetail';
import ServicesWizard from './components/ServicesWizard';
import { claimGuestOrders } from '@rehub/commerce';
import FluidBackground from './components/FluidBackground';
import PricingBenefitsModal from './components/PricingBenefitsModal';
import ShareDemoPage from '../photoclear/components/ShareDemoPage';
import { AppState, ImageState, WatermarkSettings, UserProfile, FurnitureInventory } from './types';
import { declutterImage, declutterImageWithReference, declutterImageWithRoomAnchor, editImageWithMask, extractFurnitureInventory, getLastStructureVerdict, getStagingImageModel, repairStructureRegion, upscaleImage } from './services/gemini';
import { compositeMaskedResult, compositeTopBand } from './services/maskBlend';
import { supabase, isConfigured, uploadImageToStorage, refundCredit, checkAdminStatus, getAdminRole, logEvent, checkUserGenerationPermission, createGenerationRecord, updateGenerationRecord, isAbortError, isMissingRpcError, sleep } from './services/supabase';
import { adoptSharedSession } from '@rehub/supabase/session-sync';
import {
  loadPersistedPhotoplanReturnContext,
  persistPhotoplanReturnContext,
  readPhotoplanReturnContext,
  sendBackToPhotoplan,
  type PhotoplanReturnContext,
} from './services/photoplan-return';
import {
  GPanoMeta,
  blobWithGPano,
  compositeStagedOverOriginal,
  dataUrlWithGPano,
  fileToDataUrl,
  readGPanoXmp,
  resizeTo2to1,
  resolveGPanoMeta,
  restorePolarRegions,
} from './services/equirect';
import { removeGlobalTint, isRecolorPrompt } from './services/colorMatch';
import { drawAiDisclaimer } from './utils/aiDisclaimer';
import { persistAiProvenance, getDownloadLabel } from '@rehub/ai-transparency';
import { Loader2, Sparkles, X, LogIn, UserPlus, Image as ImageIcon, RefreshCw, ArrowRight, Layers } from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { AUTO_DECLUTTER_PROMPT, APP_VERSION, CREDITS_PER_IMAGE, GENERATIONS_PER_BUNDLE, MAX_PROCESSING_DIMENSION, GEMINI_INPUT_JPEG_QUALITY, CEILING_LOCK_FRACTION, ROOM_TYPES, FURNITURE_STYLES, STAGING_TIERS, REFINE_STAGED_360_PROMPT, STAGING_FULL_RENOVATION_CREDITS, creditsForStagingTier, isFixOpeningsPrompt, isFullRenovationPrompt, shouldLockCeiling } from './constants';

const cropResultToOriginal = async (base64Result: string, restoreRect: {x: number, y: number, width: number, height: number} | null, is360: boolean = false): Promise<string> => {
  // For 360 panoramas the new pipeline sends the source unmodified (no
  // padding) and asks Gemini to match the input ratio. We don't need to
  // crop, but we DO want to guarantee the final image is exactly 2:1 in
  // case Gemini returned a slightly off ratio — `resizeTo2to1` does that
  // via bilinear resize, which preserves the seamless left/right wrap.
  if (!restoreRect) {
    if (is360) return resizeTo2to1(base64Result);
    return base64Result;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Calculate crop dimensions with rounding for pixel precision
      const targetW = Math.round(img.width * restoreRect.width);
      const targetH = Math.round(img.height * restoreRect.height);
      const startX = Math.round(img.width * restoreRect.x);
      const startY = Math.round(img.height * restoreRect.y);

      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject("Canvas context error");
        return;
      }
      
      // PIXEL-PERFECT: Disable smoothing for 1:1 crop operations (no scaling)
      // This preserves exact pixel data from Gemini's PNG output
      ctx.imageSmoothingEnabled = false;
      
      ctx.drawImage(img, startX, startY, targetW, targetH, 0, 0, targetW, targetH);
      
      if (is360) {
        const finalRatio = targetW / targetH;
        console.log(`360° image cropped - Final ratio: ${finalRatio.toFixed(4)} (target: 2.0000), dimensions: ${targetW}x${targetH}`);
      }
      
      // PIXEL-PERFECT: Use PNG to avoid JPEG compression artifacts
      // Final format conversion happens at download time based on user preference
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = base64Result;
  });
};

// Helper to get input URL from images table
const getInputUrlForJob = async (jobId: string): Promise<string | undefined> => {
  try {
    const { data } = await supabase
      .from('images')
      .select('input_url')
      .eq('id', jobId)
      .single();
    return data?.input_url || undefined;
  } catch {
    return undefined;
  }
};

const is360EquirectangularImage = (width: number, height: number): boolean => {
  const ratio = width / height;
  // 360 equirectangular images are 2:1 aspect ratio (tolerance of ±5%)
  return Math.abs(ratio - 2.0) < 0.1;
};

const getIncomingReferenceImageUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const candidate = params.get('referenceImageUrl') || params.get('reference');
  if (!candidate) return null;

  // Support either data URLs or hosted image URLs.
  if (candidate.startsWith('data:image/') || /^https?:\/\//i.test(candidate)) {
    return candidate;
  }
  return null;
};

/**
 * Picks up an `?imageUrl=` query param used by sister apps (e.g. Photoclear's
 * "Send to Virtual Staging" button) to pre-load an image as the input photo
 * to stage. Distinct from `referenceImageUrl`, which means "match this style".
 */
const getIncomingInputImageUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const candidate = params.get('imageUrl');
  if (!candidate) return null;

  if (candidate.startsWith('data:image/') || /^https?:\/\//i.test(candidate)) {
    return candidate;
  }
  return null;
};

/**
 * When opened by 360 Tours' in-tour "Stage 360°" button, the URL carries
 * `returnTo=tours&tourId=…&sceneId=…&returnOrigin=…`. We use those to
 * route the staged result back to that specific tour/scene rather than
 * letting the user create a new tour from scratch.
 *
 * Returns null when this isn't a tours hand-off session.
 */
interface ToursReturnContext {
  tourId: string;
  sceneId: string;
  returnOrigin: string;
}
const getToursReturnContext = (): ToursReturnContext | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('returnTo') !== 'tours') return null;
  const tourId = params.get('tourId');
  const sceneId = params.get('sceneId');
  const returnOriginRaw = params.get('returnOrigin') || '';
  // Only accept return origins that look like a real https tours app —
  // never trust a bare host attacker could craft via crafted links.
  let returnOrigin = '';
  try {
    const u = new URL(returnOriginRaw);
    if (u.protocol === 'https:' && u.hostname.endsWith('.rehub.studio')) {
      returnOrigin = `${u.protocol}//${u.host}`;
    }
  } catch {}
  if (!tourId || !sceneId || !returnOrigin) return null;
  return { tourId, sceneId, returnOrigin };
};

const isMultiAngleStagingPrompt = (prompt: string, hasReferenceInput: boolean): boolean => {
  if (hasReferenceInput) return true;
  // Matches the 360 stage lead ("Virtually stage this room as"), the flat stage
  // lead ("...furniture and movable decor into its empty floor space..."), and
  // the reference / match-style prompt ("You are given two images.").
  return (
    /Virtually stage this room as|furniture and movable decor into its empty floor space/i.test(prompt) ||
    /You are given two images\./i.test(prompt)
  );
};

const prepareImageForAPI = async (file: File): Promise<{
    imageData: string;
    aspectRatio: string;
    restoreRect: { x: number, y: number, width: number, height: number } | null;
    is360: boolean;
    originalWidth: number;
    originalHeight: number;
    gpanoSource: Partial<GPanoMeta> | null;
}> => {
  // Read GPano metadata (if present) up front so we can preserve FullPanoWidth/Height.
  const gpanoSource = await readGPanoXmp(file).catch(() => null);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const originalW = img.width;
      const originalH = img.height;

      // Check if this is a 360 equirectangular image
      const is360 = is360EquirectangularImage(originalW, originalH);
      
      if (is360) {
        // 360° equirectangular panoramas (2:1) are sent to Gemini AS-IS.
        //
        // Why no padding: Gemini's `aspectRatio` enum doesn't include 2:1,
        // but the `gemini-image` edge function maps any unsupported ratio
        // (including `match_input_image`) to "no aspectRatio in imageConfig",
        // which lets Gemini infer the ratio from the input. So we don't
        // need to lie to Gemini about the shape of the image.
        //
        // Previous implementation tried to "pad to 21:9" by computing
        // `paddedH = originalW / (21/9)`. For a 2:1 source that yields
        // paddedH < originalH (because 21:9 is wider than 2:1, not taller),
        // so the source's top and bottom rows were silently CROPPED off
        // the canvas before being sent to Gemini. The model then never
        // saw the ceiling/floor poles of the panorama, treated the input
        // as a regular wide-angle photo, and the output came back with
        // flat-photo perspective instead of equirectangular curvature.
        // Worse: the corresponding negative `restoreRect.y` produced
        // transparent bands at the top and bottom of the cropped result,
        // which `restorePolarRegions` then masked but couldn't fully
        // hide. Net result: visible warping, lost panorama poles, and
        // furniture that didn't reproject onto a sphere correctly.
        //
        // Sending the original 2:1 with no pad/crop fixes all of that.
        // The final 2:1 normalisation is handled by `cropResultToOriginal`
        // (when `restoreRect == null && is360`) via bilinear resize, which
        // preserves the seamless left/right wrap.
        //
        // Optional downscale to MAX_PROCESSING_DIMENSION on the long edge
        // happens here to bound memory + the data URL we hand to Gemini;
        // the source aspect ratio is preserved.
        let outW = originalW;
        let outH = originalH;
        if (outW > MAX_PROCESSING_DIMENSION) {
          outH = Math.round(outH * (MAX_PROCESSING_DIMENSION / outW));
          outW = MAX_PROCESSING_DIMENSION;
        }

        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error("Canvas context failed"));
          return;
        }

        const isScaling = outW !== originalW;
        ctx.imageSmoothingEnabled = isScaling;
        if (isScaling) ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, outW, outH);

        resolve({
          // High-quality JPEG (not PNG) to keep the edge-function payload
          // small enough to stay under the worker limit — see
          // GEMINI_INPUT_JPEG_QUALITY. The 2:1 wrap is unaffected by JPEG.
          imageData: canvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
          aspectRatio: 'match_input_image',
          restoreRect: null,
          is360: true,
          originalWidth: originalW,
          originalHeight: originalH,
          gpanoSource: gpanoSource ?? null,
        });
        return;
      }
      
      // Non-360 images: send to Gemini AS-IS (no padding, no crop), with
      // `aspect_ratio = 'match_input_image'` so the `gemini-image` edge
      // function drops aspectRatio from imageConfig and Gemini infers
      // the ratio from the input image.
      //
      // Why no padding (history): the previous code padded non-2:1
      // photos to the closest Gemini-supported enum aspect ratio
      // (1:1 / 3:4 / 4:3 / 9:16 / 16:9 / 21:9) by drawing the source
      // onto a black canvas, sent that padded image to Gemini, then
      // cropped the bars back off the result. The black bars confused
      // Gemini into treating the padded canvas as the actual scene
      // boundary, so it would re-render the room from a slightly
      // different camera angle / FOV / framing to "fill" the new
      // aspect ratio instead of doing a true in-place edit. Users
      // reported that the staged result no longer matched the input's
      // perspective. The 360 path already removed padding for the
      // same reason (see the `is360` branch above and the AGENTS.md
      // section "Why no 21:9 padding (history)"). We extend the same
      // no-pad approach to flat photos.
      //
      // Optional downscale to MAX_PROCESSING_DIMENSION on the long edge
      // happens here to bound memory + the data URL we hand to Gemini;
      // the source aspect ratio is preserved exactly.
      let outW = originalW;
      let outH = originalH;
      const longest = Math.max(outW, outH);
      if (longest > MAX_PROCESSING_DIMENSION) {
        const scale = MAX_PROCESSING_DIMENSION / longest;
        outW = Math.round(outW * scale);
        outH = Math.round(outH * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
          reject(new Error("Canvas context failed"));
          return;
      }

      const isScaling = outW !== originalW;
      ctx.imageSmoothingEnabled = isScaling;
      if (isScaling) ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, outW, outH);

      // High-quality JPEG (not PNG) keeps the edge-function payload small
      // enough to stay under the worker limit (HTTP 546) — see
      // GEMINI_INPUT_JPEG_QUALITY. q0.95 artifacts are imperceptible and
      // Gemini regenerates the scene from this input anyway.
      resolve({
        imageData: canvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY),
        aspectRatio: 'match_input_image',
        restoreRect: null,
        is360: false,
        originalWidth: originalW,
        originalHeight: originalH,
        gpanoSource: null,
      });
    };
    img.onerror = (e) => reject(e);
    img.src = URL.createObjectURL(file);
  });
};

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingBenefitsModal, setShowPricingBenefitsModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showDashboard, setShowDashboard] = useState(false);
  /**
   * Staged image URL queued for the "send to a 360 tour" picker. When set,
   * SendToTourModal renders so the user can pick a tour + scene to attach
   * the result to. Branch B of `handleSendTo360Tours`.
   */
  const [sendToTourImageUrl, setSendToTourImageUrl] = useState<string | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<'admin' | 'superadmin' | 'editor' | null>(null);
  const [appState, setAppState] = useState<AppState>(AppState.INITIALIZING);
  const intentionalSignOutRef = useRef(false);
  /** Pause shared-cookie adopt while uploads/RPCs are in flight — setSession aborts them. */
  const suppressAuthSyncRef = useRef(false);
  const sessionRefreshRef = useRef<string | null>(null);
  // Guards the Photoclear `?imageUrl=` import effect from running twice
  // (e.g. if React strict-mode or a re-render fires the effect again).
  const importStartedRef = useRef(false);
  const [images, setImages] = useState<ImageState>({
    originalFile: null,
    originalUrl: null,
    processedUrl: null,
    maskDataUrl: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [isUploadingOriginal, setIsUploadingOriginal] = useState(false);
  const [bundleGenerationsRemaining, setBundleGenerationsRemaining] = useState(0);
  const [generationBundleKind, setGenerationBundleKind] = useState<'staging' | 'renovation' | null>(null);
  const [isConsumingCredit, setIsConsumingCredit] = useState(false);
  /**
   * One-time notice shown the first time a user reaches a staged result.
   * AI staging isn't always perfect on the first pass, so we tell users
   * up-front they can go back and regenerate up to GENERATIONS_PER_BUNDLE
   * times per photo without spending more credits. Dismissal is persisted
   * in localStorage so it only ever appears once per browser.
   */
  const [showPassesNotice, setShowPassesNotice] = useState(false);
  /**
   * One-shot override of the source image used for polar restore + tint
   * anchor + GPano source inside `handleProcess`. Set by `handleRequestChanges`
   * to the previously-staged 360 panorama so a refinement pass uses the
   * staged image (not the empty source room) as the "ground truth" to
   * preserve. Cleared by handleProcess when it consumes the override.
   *
   * Why a ref and not state: handleProcess closes over `images.originalFile`
   * and runs synchronously after the call site sets this — a state update
   * wouldn't have flushed in time. A ref reads its current value at the
   * exact moment handleProcess runs.
   */
  const refinementSourceRef = useRef<File | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  /** When set, next staging can "match this image" (from dashboard or upload). Cleared on reset. */
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);

  // === SAME-ROOM MULTI-ANGLE BATCH ===
  // When the user uploads several angles of the same room at once, they pick
  // a hero overview; the rest are queued. Once the hero is staged, its
  // full-resolution result becomes `sameRoomAnchorUrl`, a furniture inventory
  // is extracted, and each queued angle is staged against both (high-res
  // layout anchor + checklist) for cross-angle furniture consistency.
  // Distinct from `referenceImageUrl`, which is the legacy low-res style-swatch flow.
  /** Remaining same-room angles still waiting to be staged. */
  const [angleQueue, setAngleQueue] = useState<File[]>([]);
  /** High-res staged hero used as the layout anchor for follow-up angles. */
  const [sameRoomAnchorUrl, setSameRoomAnchorUrl] = useState<string | null>(null);
  /** Total angles in the current batch (for "angle X of N" progress copy). */
  const [sameRoomTotal, setSameRoomTotal] = useState(0);
  /**
   * Angle indices (0 = hero) that have been successfully staged at least once.
   * Regenerations do not inflate this — fixes premature "all done" banners.
   */
  const [sameRoomCompletedIndices, setSameRoomCompletedIndices] = useState<number[]>([]);
  /** Index of the angle currently loaded in the editor (0 = hero). */
  const [sameRoomCurrentIndex, setSameRoomCurrentIndex] = useState(0);
  /** Structured furniture list from the staged hero for follow-up placement. */
  const [sameRoomInventory, setSameRoomInventory] = useState<FurnitureInventory | null>(null);
  /**
   * Set when the staged hero failed the structural inspection (re-rendered
   * room). A broken hero must NOT become the batch anchor — every follow-up
   * would be asked to copy furniture from a room that doesn't exist.
   */
  const [sameRoomAnchorWarning, setSameRoomAnchorWarning] = useState<string | null>(null);
  /**
   * Set when Full Renovation (or staging) still fails structural inspection
   * after retries. Surfaces a banner so the user can regenerate or brush-fix
   * invented openings.
   */
  const [structureWarning, setStructureWarning] = useState<string | null>(null);
  /** Same-room angles to resume after a logged-out user signs in. */
  const [pendingQueue, setPendingQueue] = useState<File[]>([]);
  /** Auto-advance timer for follow-up angles after a successful stage. */
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Image URL passed in via `?imageUrl=` (e.g. from Photoclear's "Send to Virtual
   * Staging" button). We fetch it once auth has resolved and load it as the
   * input photo, then jump straight into the editor.
   */
  const [pendingIncomingImageUrl, setPendingIncomingImageUrl] = useState<string | null>(null);
  /**
   * True while we're actively fetching/loading an image handed off from
   * another app (e.g. Photoclear). Used to render a clear loading overlay so
   * the user doesn't see the Upload page flash by while we import.
   */
  const [isImportingFromExternal, setIsImportingFromExternal] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const candidate = params.get('imageUrl');
    if (!candidate) return false;
    return candidate.startsWith('data:image/') || /^https?:\/\//i.test(candidate);
  });
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(ROOM_TYPES[0].id);
  const [selectedFurnitureStyle, setSelectedFurnitureStyle] = useState(FURNITURE_STYLES[0].id);
  const [selectedStagingTier, setSelectedStagingTier] = useState(STAGING_TIERS[0].id);
  const [processingKind, setProcessingKind] = useState<'stage' | 'renovate'>('stage');
  const [lastOutputUrl, setLastOutputUrl] = useState<string | null>(null);
  const [lastStagingImageId, setLastStagingImageId] = useState<string | null>(null);
  const [photoplanReturn, setPhotoplanReturn] = useState<PhotoplanReturnContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    let callbackTimer: ReturnType<typeof setTimeout> | null = null;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

    const initApp = async () => {
        const incomingReferenceImageUrl = getIncomingReferenceImageUrl();
        if (incomingReferenceImageUrl) {
          setReferenceImageUrl(incomingReferenceImageUrl);

          // Remove one-time reference params from URL after capture.
          const url = new URL(window.location.href);
          url.searchParams.delete('referenceImageUrl');
          url.searchParams.delete('reference');
          window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        }

        const incomingInputImageUrl = getIncomingInputImageUrl();
        const photoplanCtx = readPhotoplanReturnContext() ?? loadPersistedPhotoplanReturnContext();
        if (photoplanCtx) {
          persistPhotoplanReturnContext(photoplanCtx);
          setPhotoplanReturn(photoplanCtx);
        }
        if (incomingInputImageUrl) {
          setPendingIncomingImageUrl(incomingInputImageUrl);

          // Remove the param so a refresh doesn't re-trigger import.
          const url = new URL(window.location.href);
          url.searchParams.delete('imageUrl');
          url.searchParams.delete('returnTo');
          url.searchParams.delete('returnOrigin');
          url.searchParams.delete('returnPath');
          url.searchParams.delete('bookingId');
          url.searchParams.delete('assetId');
          url.searchParams.delete('source');
          window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        }

        // Image processing now runs through the `replicate-nano-banana-pro`
        // Supabase Edge Function (auth'd via the user's Supabase session), so
        // the legacy Gemini-key gate is no longer needed — go straight to UPLOAD.
        setAppState(AppState.UPLOAD);

        if (!isConfigured) {
          setAuthLoading(false);
          return;
        }

        if (window.location.pathname === '/dashboard') {
            setShowDashboard(true);
        }

        const isAuthCallback = window.location.pathname === '/auth/callback';
        
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        const hasAuthTokens = hashParams.get('access_token') || queryParams.get('code');
        
        const ssoAccessToken = hashParams.get('sso_access_token');
        const ssoRefreshToken = hashParams.get('sso_refresh_token');
        
        if (ssoAccessToken && ssoRefreshToken) {
          window.history.replaceState({}, document.title, window.location.pathname);
          
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: ssoAccessToken,
              refresh_token: ssoRefreshToken
            });
            
            if (data.session && !cancelled) {
              setSession(data.session);
              fetchProfile(data.session.user.id);
              setAuthLoading(false);
              return;
            } else if (error) {
              console.warn('SSO session failed:', error.message);
            }
          } catch (err) {
            console.warn('SSO session error:', err);
          }
        }

        if (isAuthCallback) {
          callbackTimer = setTimeout(() => {
            if (!cancelled && window.location.pathname === '/auth/callback') {
              console.warn('Auth callback timeout - redirecting to home');
              window.history.replaceState({}, document.title, "/");
              setCurrentPath("/");
              setAuthLoading(false);
            }
          }, 8000);
        }

        if (hasAuthTokens && isAuthCallback) {
          // Let Supabase handle PKCE exchange via onAuthStateChange
          return;
        }

        try {
          const { data: { session: existingSession } } = await supabase.auth.getSession();
          if (cancelled) return;

          if (!existingSession) {
            if (!isAuthCallback) setAuthLoading(false);
            return;
          }

          // Validate the JWT server-side to catch expired/revoked tokens
          const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser();
          if (cancelled) return;

          if (validatedUser && !userError) {
            setSession(existingSession);
            if (isAuthCallback) {
              window.history.replaceState({}, document.title, "/");
              setCurrentPath("/");
              fetchProfile(validatedUser.id, true);
            } else {
              fetchProfile(validatedUser.id);
            }
          } else {
            // Token invalid — attempt refresh before giving up
            const { data: refreshData } = await supabase.auth.refreshSession();
            if (cancelled) return;

            if (refreshData?.session) {
              setSession(refreshData.session);
              fetchProfile(refreshData.session.user.id);
            } else {
              setAuthLoading(false);
            }
          }
        } catch {
          if (!cancelled && !isAuthCallback) setAuthLoading(false);
        }
    };

    initApp();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (cancelled) return;

      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED': {
          setSession(newSession);
          if (callbackTimer) { clearTimeout(callbackTimer); callbackTimer = null; }

          if (newSession) {
            const pendingConsent = localStorage.getItem('pending_marketing_consent');
            if (pendingConsent === 'true') {
              localStorage.removeItem('pending_marketing_consent');
              await supabase
                .from('profiles')
                .update({
                  marketing_consent: true,
                  marketing_consent_at: new Date().toISOString()
                })
                .eq('id', newSession.user.id);
            } else if (pendingConsent === 'false') {
              localStorage.removeItem('pending_marketing_consent');
            }

            setShowLoginModal(false);

            const pendingRef = localStorage.getItem('referral_code');
            if (pendingRef) {
              localStorage.removeItem('referral_code');
              const provider = newSession.user?.app_metadata?.provider;
              const authMethod =
                provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
              void supabase
                .rpc('apply_referral_code', {
                  p_code: pendingRef,
                  p_auth_method: authMethod,
                  p_app_source: 'staging',
                })
                .catch(() => {});
            }

            if (pendingFile) {
              fetchProfile(newSession.user.id);
              const file = pendingFile;
              const queue = pendingQueue;
              setPendingFile(null);
              setPendingQueue([]);
              setShowLoginPrompt(false);
              setTimeout(() => {
                if (queue.length > 0) {
                  // Resume the full same-room batch the user started before login.
                  setReferenceImageUrl(null);
                  setAngleQueue(queue);
                  setSameRoomAnchorUrl(null);
                  setSameRoomInventory(null);
                  setSameRoomTotal(queue.length + 1);
                  setSameRoomCompletedIndices([]);
                  setSameRoomCurrentIndex(0);
                }
                const url = URL.createObjectURL(file);
                setImages({
                  originalFile: file,
                  originalUrl: url,
                  processedUrl: null,
                  maskDataUrl: null,
                });
                setAppState(AppState.EDITOR);
                setBundleGenerationsRemaining(0);
              }, 100);
              return;
            }

            const path = window.location.pathname;
            if (path === '/auth/callback' || path === '/login') {
              window.history.replaceState({}, document.title, "/");
              setCurrentPath("/");
              fetchProfile(newSession.user.id, true);
            } else if (path === '/dashboard') {
              fetchProfile(newSession.user.id, true);
            } else {
              fetchProfile(newSession.user.id);
            }
          }
          break;
        }

        case 'SIGNED_OUT': {
          if (intentionalSignOutRef.current) {
            intentionalSignOutRef.current = false;
            setSession(null);
            setUserProfile(null);
            setAuthLoading(false);
            setShowDashboard(false);
            break;
          }
          // Spurious sign-out: sibling app rotated the shared cookie. Never
          // refreshSession here — that burns the token family across apps.
          if (recoveryTimer) clearTimeout(recoveryTimer);
          recoveryTimer = setTimeout(async () => {
            for (let attempt = 1; attempt <= 4; attempt++) {
              if (cancelled) return;
              try {
                const adopted = await adoptSharedSession(supabase, sessionRefreshRef.current);
                if (adopted?.access_token) {
                  setSession(adopted);
                  setAuthLoading(false);
                  fetchProfile(adopted.user.id);
                  return;
                }
              } catch { /* retry */ }
              await new Promise((r) => setTimeout(r, 2000));
            }
            if (cancelled) return;
            setSession(null);
            setUserProfile(null);
            setAuthLoading(false);
            setShowDashboard(false);
          }, 400);
          break;
        }

        case 'INITIAL_SESSION': {
          if (newSession) {
            setSession(newSession);
          }
          break;
        }
      }
    });

    return () => {
      cancelled = true;
      if (callbackTimer) clearTimeout(callbackTimer);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    sessionRefreshRef.current = session?.refresh_token ?? null;
  }, [session?.refresh_token]);

  useEffect(() => {
    if (!isConfigured) return;
    let busy = false;
    const sync = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      if (intentionalSignOutRef.current || suppressAuthSyncRef.current) return;
      busy = true;
      try {
        const adopted = await adoptSharedSession(supabase, sessionRefreshRef.current);
        if (suppressAuthSyncRef.current) return;
        if (adopted?.access_token) {
          setSession((prev) =>
            prev?.refresh_token === adopted.refresh_token ? prev : adopted,
          );
        }
      } catch (err) {
        if (!isAbortError(err)) {
          console.warn('[auth] visibility sync failed:', err);
        }
      } finally {
        busy = false;
      }
    };
    const onFocus = () => { void sync(); };
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (!session?.user) return;

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.new) {
              setUserProfile(payload.new as UserProfile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session]);

  const fetchProfile = async (userId: string, showDashboardAfter: boolean = false) => {
    try {
      await claimGuestOrders(supabase, userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      setUserProfile(data);
      
      // Check admin status and role
      const role = await getAdminRole(userId);
      setAdminRole(role);
      setIsAdmin(!!role);
    } catch (error) {
      console.error('Error fetching profile:', error);
      const currentUser = (await supabase.auth.getUser()).data.user;
      if (currentUser) {
          setUserProfile({ id: userId, email: currentUser.email || '', credits: 10 });
      }
    } finally {
      setAuthLoading(false);
      // Show dashboard after profile is loaded (for auth callbacks)
      if (showDashboardAfter) {
        setShowDashboard(true);
      }
    }
  };

  const handleLogout = async () => {
    try {
      intentionalSignOutRef.current = true;
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
      intentionalSignOutRef.current = false;
    }
  };

  const handleOpenDashboard = () => {
      if (session && userProfile) {
          setShowDashboard(true);
      } else {
          setAuthMode('login');
          setShowLoginModal(true);
      }
  };

  // When an external app (e.g. Photoclear) hands us an `?imageUrl=`, fetch
  // the blob and load it into the editor as a local File. We do NOT pre-show
  // the editor with the remote URL because the canvas would become
  // CORS-tainted, blocking later toDataURL() exports needed for processing.
  // We use a ref-based guard (not the effect's `cancelled` flag) so the
  // import survives the state updates we make during the work.
  useEffect(() => {
    if (!pendingIncomingImageUrl) return;
    if (importStartedRef.current) return;
    importStartedRef.current = true;

    const remoteUrl = pendingIncomingImageUrl;
    console.log('[VSS import] Starting fetch for:', remoteUrl);

    (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      try {
        const res = await fetch(remoteUrl, { credentials: 'omit', signal: controller.signal });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const blob = await res.blob();
        const mime = blob.type || 'image/png';
        const ext = (mime.split('/')[1] || 'png').split(';')[0];
        const file = new File([blob], `from-photoclear-${Date.now()}.${ext}`, { type: mime });

        // Probe dimensions (from a same-origin object URL — never CORS tainted).
        const probeUrl = URL.createObjectURL(blob);
        const dims = await new Promise<{ width: number; height: number }>((resolve) => {
          const probe = new Image();
          probe.onload = () => resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
          probe.onerror = () => resolve({ width: 0, height: 0 });
          probe.src = probeUrl;
        });
        URL.revokeObjectURL(probeUrl);

        const is360 = dims.width > 0 && is360EquirectangularImage(dims.width, dims.height);
        const objectUrl = URL.createObjectURL(file);

        console.log('[VSS import] Loading file into editor', { dims, is360 });
        setShowDashboard(false);
        setImages((prev) => ({
          ...prev,
          originalFile: file,
          originalUrl: objectUrl,
          processedUrl: null,
          maskDataUrl: null,
          is360,
        }));
        setAppState(AppState.EDITOR);
        setBundleGenerationsRemaining(0);
      } catch (err) {
        console.error('[VSS import] Failed to import incoming image URL:', err);
        alert('Could not load the image from Photoclear. Please upload it manually.');
      } finally {
        clearTimeout(timeoutId);
        setIsImportingFromExternal(false);
        setPendingIncomingImageUrl(null);
      }
    })();
    // We intentionally only depend on `pendingIncomingImageUrl`. The
    // `importStartedRef` guard prevents duplicate work if React re-runs
    // the effect for any reason.
  }, [pendingIncomingImageUrl]);

  const handleImageSelected = (file: File) => {
    // Check if user is logged in
    if (!session) {
      setPendingFile(file);
      setShowLoginPrompt(true);
      return;
    }

    // Gallery / incomplete batch: keep the anchor/inventory and treat this
    // upload as the next angle. A finished batch (all indices completed) or a
    // fresh upload clears same-room state first.
    const resumingSameRoom =
      !!sameRoomAnchorUrl &&
      sameRoomTotal > 0 &&
      angleQueue.length === 0 &&
      sameRoomCompletedIndices.length < sameRoomTotal;
    if (resumingSameRoom) {
      setSameRoomCurrentIndex(Math.max(sameRoomCompletedIndices.length, 1));
      setSameRoomTotal((t) => Math.max(t, sameRoomCompletedIndices.length + 1));
      loadFileIntoEditor(file);
      return;
    }

    clearSameRoomBatch();

    // Detect 360° equirectangular so we can flip the editor into panorama mode.
    // No modal interruption — the Editor surfaces a banner explaining what's supported.
    const img = new Image();
    img.onload = () => {
      const is360 = is360EquirectangularImage(img.width, img.height);

      setShowDashboard(false);
      const url = URL.createObjectURL(file);
      setImages({
        ...images,
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        is360,
      });
      setAppState(AppState.EDITOR);
      setBundleGenerationsRemaining(0);
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  };

  /** Reset all same-room batch state (used on start-over and single uploads). */
  const clearSameRoomBatch = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    setAngleQueue([]);
    setSameRoomAnchorUrl(null);
    setSameRoomInventory(null);
    setSameRoomTotal(0);
    setSameRoomCompletedIndices([]);
    setSameRoomCurrentIndex(0);
    setSameRoomAnchorWarning(null);
  };

  /** Load a File into the editor as the active image, detecting 360° panoramas. */
  const loadFileIntoEditor = (file: File) => {
    setShowDashboard(false);
    setStructureWarning(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const is360 = is360EquirectangularImage(img.width, img.height);
      setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null,
        is360,
      });
      setAppState(AppState.EDITOR);
      setBundleGenerationsRemaining(0);
    };
    img.onerror = () => {
      setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null,
      });
      setAppState(AppState.EDITOR);
      setBundleGenerationsRemaining(0);
    };
    img.src = url;
  };

  /**
   * Multiple angles of the SAME room uploaded together (hero already first
   * after UploadZone's hero picker). Stage the hero, queue the rest. Once the
   * hero is staged, inventory + anchor drive consistent furniture on follow-ups.
   */
  const handleImagesSelected = (files: File[]) => {
    if (files.length === 0) return;
    if (files.length === 1) {
      handleImageSelected(files[0]);
      return;
    }
    if (!session) {
      // Resume the whole batch after sign-in.
      setPendingFile(files[0]);
      setPendingQueue(files.slice(1));
      setShowLoginPrompt(true);
      return;
    }
    setReferenceImageUrl(null);
    setAngleQueue(files.slice(1));
    setSameRoomAnchorUrl(null);
    setSameRoomInventory(null);
    setSameRoomTotal(files.length);
    setSameRoomCompletedIndices([]);
    setSameRoomCurrentIndex(0);
    loadFileIntoEditor(files[0]);
  };

  /**
   * Advance to the next queued same-room angle. Keeps `sameRoomAnchorUrl` (the
   * staged hero) so the editor's "Stage This Angle" button anchors to it.
   */
  const handleStageNextAngle = () => {
    if (angleQueue.length === 0) return;
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    const [next, ...rest] = angleQueue;
    const nextIndex = sameRoomTotal - rest.length - 1;
    setAngleQueue(rest);
    setSameRoomCurrentIndex(nextIndex);
    setError(null);
    loadFileIntoEditor(next);
  };

  /** Start a same-room follow-up from a gallery staged image (layout, not style). */
  const handleStageAnotherAngleOfRoom = async (outputUrl: string) => {
    setReferenceImageUrl(null);
    setAngleQueue([]);
    setSameRoomAnchorUrl(outputUrl);
    setSameRoomTotal(2);
    setSameRoomCompletedIndices([0]);
    setSameRoomCurrentIndex(1);
    setSameRoomInventory(null);
    setShowDashboard(false);
    setAppState(AppState.UPLOAD);
    // Best-effort inventory extract so follow-up placement has a checklist.
    try {
      const inventory = await extractFurnitureInventory(outputUrl);
      if (inventory) setSameRoomInventory(inventory);
    } catch (e) {
      console.warn('Could not extract inventory from gallery image:', e);
    }
  };

  // Handle proceeding after login with pending file
  const handleLoginPromptAuth = (mode: 'login' | 'signup') => {
    setShowLoginPrompt(false);
    setAuthMode(mode);
    setShowLoginModal(true);
  };

  const consumeCredits = async (amount: number = CREDITS_PER_IMAGE): Promise<'success' | 'insufficient_credits' | 'error'> => {
    if (!session?.user) return 'error';

    if (userProfile && userProfile.credits < amount) {
      return 'insufficient_credits';
    }

    const applyLocalDeduct = () => {
      if (userProfile) {
        setUserProfile(prev => prev ? { ...prev, credits: prev.credits - amount } : null);
      }
    };

    const tryLegacyLoop = async (): Promise<'success' | 'insufficient_credits' | 'error' | 'aborted'> => {
      for (let i = 0; i < amount; i++) {
        const { error } = await supabase.rpc('consume_credit', { user_id: session.user.id });
        if (!error) continue;

        for (let j = 0; j < i; j++) {
          await refundCredit(session.user.id);
        }
        if (isAbortError(error)) return 'aborted';
        console.error('Credit consumption error:', error);
        const msg = String(error.message || error.code || error);
        if (/INSUFFICIENT_CREDITS/i.test(msg)) return 'insufficient_credits';
        return 'error';
      }
      applyLocalDeduct();
      return 'success';
    };

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Prefer multi-credit RPC (one round-trip).
        const { data: multiOk, error: multiErr } = await supabase.rpc('consume_credits', {
          p_user_id: session.user.id,
          p_amount: amount,
        });

        if (!multiErr) {
          if (multiOk === false) return 'insufficient_credits';
          applyLocalDeduct();
          return 'success';
        }

        // Auth refresh aborts in-flight RPCs — retry; do not treat as missing RPC.
        if (isAbortError(multiErr)) {
          console.warn(`consume_credits aborted (attempt ${attempt}/${maxAttempts}), retrying…`);
          if (attempt < maxAttempts) {
            await sleep(400 * attempt);
            continue;
          }
          return 'error';
        }

        if (!isMissingRpcError(multiErr)) {
          console.error('Credit consumption error:', multiErr);
          const msg = String(multiErr.message || multiErr.code || multiErr);
          if (/INSUFFICIENT_CREDITS/i.test(msg)) return 'insufficient_credits';
          return 'error';
        }

        console.warn('consume_credits unavailable, falling back to consume_credit loop:', multiErr);
        const legacy = await tryLegacyLoop();
        if (legacy === 'aborted') {
          console.warn(`consume_credit aborted (attempt ${attempt}/${maxAttempts}), retrying…`);
          if (attempt < maxAttempts) {
            await sleep(400 * attempt);
            continue;
          }
          return 'error';
        }
        return legacy;
      } catch (err) {
        if (isAbortError(err) && attempt < maxAttempts) {
          console.warn(`Credit RPC aborted (attempt ${attempt}/${maxAttempts}), retrying…`);
          await sleep(400 * attempt);
          continue;
        }
        console.error('Credit RPC failed:', err);
        return 'error';
      }
    }

    return 'error';
  };

  // --- CONTINUE EDITING ---
  const handleContinueEditing = (file: File) => {
    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const is360 = is360EquirectangularImage(img.width, img.height);
      setImages({
          originalFile: file,
          originalUrl: url,
          processedUrl: null,
          maskDataUrl: null,
          is360,
      });
      setAppState(AppState.EDITOR);
      setBundleGenerationsRemaining(0);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      setImages({
          originalFile: file,
          originalUrl: url,
          processedUrl: null,
          maskDataUrl: null,
      });
      setAppState(AppState.EDITOR);
      setBundleGenerationsRemaining(0);
    };
    img.src = URL.createObjectURL(file);
  };

  // --- AUTO PROCESS ---
  const handleAutoProcess = async (file: File) => {
    if (!userProfile) return;
    
    // Prevent double-click race condition
    if (isConsumingCredit) {
        console.log('Blocked duplicate auto-process call');
        return;
    }
    
    // Charge only when the current staging bundle is exhausted.
    let chargedThisRequest = false;
    let availableGenerations = bundleGenerationsRemaining;
    if (availableGenerations <= 0 || generationBundleKind !== 'staging') {
      setIsConsumingCredit(true);
      const creditResult = await consumeCredits(CREDITS_PER_IMAGE);
      setIsConsumingCredit(false);
      if (creditResult !== 'success') {
        if (creditResult === 'insufficient_credits') {
          setShowBuyCreditsModal(true);
        } else {
          alert("Transaction failed. Please try again.");
        }
        return;
      }
      chargedThisRequest = true;
      availableGenerations = GENERATIONS_PER_BUNDLE;
    }

    setShowDashboard(false);
    const url = URL.createObjectURL(file);
    setImages({
        originalFile: file,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null
    });
    setAppState(AppState.PROCESSING);
    suppressAuthSyncRef.current = true;
    
    // 2. Generate Job ID for Gallery Pairing
    const jobId = crypto.randomUUID();
    
    // 3. Create generation record for tracking
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: 'auto', job_id: jobId, source_app: 'staging', ai_operation: 'virtual_staging' }
    );

    // 4. TRY BLOCK FOR PROCESS & UPLOAD
    let inputUrl: string | undefined;
    try {
        // Upload Original
        if (session?.user) {
             // IMPORTANT: Insert record BEFORE upload so trigger doesn't create with wrong service_type
             console.log('STAGING: Pre-inserting with service_type: staging', { jobId });
             await supabase.from('images').upsert({
                    id: jobId,
                    user_id: session.user.id,
                    input_url: '',
                    output_url: '',
                    status: 'processing',
                    type: 'restoration',
                    service_type: 'staging'
             });

             try {
                 inputUrl = await uploadImageToStorage(session.user.id, file, 'original', jobId);
                 if (inputUrl) {
                    await supabase.from('images').update({
                        input_url: inputUrl
                    }).eq('id', jobId);
                 }
             } catch (uploadErr) {
                 console.warn('STAGING: input upload failed; continuing with local image', uploadErr);
             }
        }

        const { imageData, aspectRatio, restoreRect, is360, originalWidth, originalHeight, gpanoSource } = await prepareImageForAPI(file);
        
        // Show warning for 360 images
        if (is360) {
          console.log('Processing 360 equirectangular image using 21:9 workaround, will crop back to 2:1');
        }
        
        // Client-side Gemini processing
        const resultBase64 = await declutterImage(imageData, AUTO_DECLUTTER_PROMPT, aspectRatio);
        let croppedResult = await cropResultToOriginal(resultBase64, restoreRect, is360);

        // Cancel any global colour cast Gemini may have introduced (e.g.
        // magenta/purple drift). Anchored to the ORIGINAL upload's white
        // point so iterative passes stop compounding.
        try {
            const originalSourceDataUrl = await fileToDataUrl(file);
            const tintFix = await removeGlobalTint(originalSourceDataUrl, croppedResult);
            if (tintFix.method !== 'none') {
                console.log(
                    `Tint fix (auto, ${tintFix.method}): anchors=${tintFix.anchorCount} ` +
                    `gains=R${tintFix.gains.r.toFixed(3)} ` +
                    `G${tintFix.gains.g.toFixed(3)} ` +
                    `B${tintFix.gains.b.toFixed(3)}`
                );
            }
            croppedResult = tintFix.dataUrl;
        } catch (tintErr) {
            console.warn('Tint removal (auto) failed:', tintErr);
        }

        // For 360 panoramas: hard-restore the original's polar bands (zenith/nadir)
        // to eliminate any pole-ring artifact, then re-inject GPano XMP.
        if (is360) {
            try {
                const sourceDataUrl = await fileToDataUrl(file);

                // Resolution recovery (see compositeStagedOverOriginal): keep the
                // full-resolution original everywhere Gemini didn't change and
                // only take the 4K staged pixels where furniture/decor was added.
                try {
                    croppedResult = await compositeStagedOverOriginal(
                        sourceDataUrl,
                        croppedResult
                    );
                } catch (compositeErr) {
                    console.warn('Full-res 360 composite (auto) failed, using Gemini output:', compositeErr);
                }

                croppedResult = await restorePolarRegions(
                    croppedResult,
                    sourceDataUrl,
                    { outputType: 'image/jpeg' }
                );
            } catch (polarErr) {
                console.warn('Polar restore (auto) failed:', polarErr);
            }
            try {
                // Read actual final dims rather than trusting the source upload's
                // dims: the new no-pad pipeline lets `cropResultToOriginal` resize
                // to 2:1 at whatever resolution Gemini returned, which may differ
                // from the user's source. GPano `CroppedAreaImageWidth/Height`
                // MUST match the file's real pixel dims or 360 viewers compute
                // wrong sphere mappings. (`resolveGPanoMeta` still preserves the
                // source's `FullPanoWidth/Height` via `gpanoSource`.)
                const dims = await getImageDimensions(croppedResult);
                const meta = resolveGPanoMeta(dims.width, dims.height, gpanoSource);
                croppedResult = dataUrlWithGPano(croppedResult, meta);
            } catch (xmpErr) {
                console.warn('GPano XMP injection (auto) failed:', xmpErr);
            }
        }

        // Upload Result and Update DB
        let outputUrl: string | undefined;
        if (session?.user) {
             try {
               outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
               if (outputUrl) {
                  await supabase.from('images').update({
                      output_url: outputUrl,
                      status: 'completed',
                      type: 'restoration'
                  }).eq('id', jobId);
               }
             } catch (uploadErr) {
               console.warn('STAGING: output upload failed; keeping local result', uploadErr);
             }
        }

        if (!croppedResult) {
            throw new Error('Processing returned empty result');
        }
        
        // Record bundle charge only on the generation that triggered payment.
        if (generationId) {
            await updateGenerationRecord(
              generationId,
              'completed',
              chargedThisRequest ? CREDITS_PER_IMAGE : 0,
              undefined,
              outputUrl,
              inputUrl
            );
        }

        try {
          if (generationId) {
            await persistAiProvenance({
              supabase,
              target: 'generations',
              id: generationId,
              app: 'staging',
              operation: 'virtual_staging',
              sourceAssetId: inputUrl || jobId,
              outputAssetId: outputUrl || jobId,
              provider: 'google',
              model: 'gemini-3-pro-image-preview',
            });
          }
          if (session?.user) {
            await persistAiProvenance({
              supabase,
              target: 'images',
              id: jobId,
              app: 'staging',
              operation: 'virtual_staging',
              sourceAssetId: inputUrl || jobId,
              outputAssetId: outputUrl || jobId,
              provider: 'google',
              model: 'gemini-3-pro-image-preview',
            });
          }
        } catch (provenanceErr) {
          console.warn('AI provenance persist (auto) failed:', provenanceErr);
        }

        setGenerationBundleKind('staging');
        setBundleGenerationsRemaining(Math.max(availableGenerations - 1, 0));
        
        // Processing complete - go back to Dashboard
        setIsConsumingCredit(false);
        setAppState(AppState.UPLOAD);
        setTimeout(() => setShowDashboard(true), 200);
    } catch (err: any) {
        console.error("Auto Process failed:", err);
        
        // Failed generation records should not show credits used.
        if (generationId) {
            await updateGenerationRecord(generationId, 'failed', 0, err.message);
        }
        
        // Refund only if this request charged a new bundle.
        if (session?.user) {
            if (chargedThisRequest) {
              for (let i = 0; i < CREDITS_PER_IMAGE; i++) {
                  await refundCredit(session.user.id);
              }
              setUserProfile(prev => prev ? { ...prev, credits: prev.credits + CREDITS_PER_IMAGE } : null);
            }
            await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
        }
        
        setError(chargedThisRequest
          ? "Processing failed. Your credits have been refunded. Please try again."
          : "Processing failed. Please try again.");
        setAppState(AppState.EDITOR);
        setIsConsumingCredit(false);
    } finally {
      suppressAuthSyncRef.current = false;
    }
  };

  /** Read pixel dimensions of any image URL or data URL. */
  const getImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const probe = new Image();
      probe.onload = () => resolve({ width: probe.width, height: probe.height });
      probe.onerror = (e) => reject(e);
      probe.src = url;
    });

  /** Fetch image URL to base64 data URL (for remote or data URLs). */
  const imageUrlToBase64 = async (url: string): Promise<string> => {
    if (url.startsWith('data:')) return url;
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Prepare the "match this image" reference before handing it to Gemini.
   *
   * The reference is downscaled to a small thumbnail (max 384px on the
   * longest edge) so Gemini reads it as a "style swatch" rather than a
   * second scene it can pixel-transplant onto the target. This is the
   * single biggest lever against the failure mode the user reported —
   * "match this image" producing a result that looks like the reference
   * with the empty room patched in, or with the camera shifted to match
   * the reference's framing.
   *
   * Why this works:
   *   - The target image is sent at full processing resolution (up to
   *     `MAX_PROCESSING_DIMENSION`, currently 4096px) — it carries
   *     enough pixels for Gemini to do a true in-place edit on it.
   *   - The reference is sent at ~384px — too few pixels to copy
   *     architectural geometry from (walls, windows, doors, framing),
   *     but more than enough to convey style cues (furniture types,
   *     colours, materials, finishes, rough arrangement).
   *   - The resolution asymmetry creates an implicit signal — the
   *     hi-res image is the canvas, the lo-res image is the mood-board.
   *
   * Note: the prompts (`MATCH_STYLE_PROMPT` / `MATCH_STYLE_360_PROMPT`)
   * also call this asymmetry out explicitly so the model can reason
   * about it in words as well as pixels.
   */
  const REFERENCE_THUMBNAIL_MAX_DIM = 384;
  const prepareReferenceForGemini = async (url: string): Promise<string> => {
    const fullDataUrl = await imageUrlToBase64(url);
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const longest = Math.max(img.width, img.height);
        if (longest <= REFERENCE_THUMBNAIL_MAX_DIM) {
          resolve(fullDataUrl);
          return;
        }
        const scale = REFERENCE_THUMBNAIL_MAX_DIM / longest;
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context failed for reference downscale'));
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (e) => reject(e);
      img.src = fullDataUrl;
    });
  };

  /**
   * Prepare the same-room ANCHOR (the staged hero) for Gemini.
   *
   * Unlike `prepareReferenceForGemini` (which crushes the reference to a 384px
   * style swatch so the model only borrows the "look"), here we keep the
   * anchor large enough to read the actual furniture LAYOUT — which piece sits
   * where — because cross-angle consistency depends on it. We still cap the
   * long edge to bound the payload; the anchor doesn't need to be full 4K.
   */
  const ANCHOR_MAX_DIM = 1536;
  const prepareAnchorForGemini = async (url: string): Promise<string> => {
    const fullDataUrl = await imageUrlToBase64(url);
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const longest = Math.max(img.width, img.height);
        if (longest <= ANCHOR_MAX_DIM) {
          resolve(fullDataUrl);
          return;
        }
        const scale = ANCHOR_MAX_DIM / longest;
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context failed for anchor downscale'));
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (e) => reject(e);
      img.src = fullDataUrl;
    });
  };

  // --- MANUAL PROCESS ---
  const handleProcess = async (
    maskedImageData: string, 
    prompt: string, 
    aspectRatio: string, 
    restoreRect: {x: number, y: number, width: number, height: number} | null,
    is360?: boolean,
    referenceImageUrlParam?: string | null,
    /**
     * Optional standalone binary mask aligned to `maskedImageData` (i.e. at
     * the same padded dimensions). When provided, `maskedImageData` is
     * expected to be the CLEAN source (no red overlay) and we route to
     * Gemini's mask-aware flow instead of the single-image flow.
     */
    maskImageData?: string | null,
    /**
     * Optional same-room ANCHOR image URL (the staged hero). When provided,
     * this angle is staged to reproduce the anchor's furniture layout via the
     * high-resolution anchor flow (cross-angle consistency).
     */
    roomAnchorUrlParam?: string | null,
    creditCostOverride?: number
  ) => {
    if (isConsumingCredit) return;

    const renovation = isFullRenovationPrompt(prompt);
    const requestedBundleKind = renovation ? 'renovation' : 'staging';
    setProcessingKind(renovation ? 'renovate' : 'stage');
    const stagingCost = creditCostOverride ?? creditsForStagingTier(selectedStagingTier);
    let chargedThisRequest = false;
    let availableGenerations = bundleGenerationsRemaining;
    const mustChargeNewBundle =
      availableGenerations <= 0 || generationBundleKind !== requestedBundleKind;
    // Charge normally for production (Sunburst). Waive only when admin/dev
    // overrides to a Gemini A/B model.
    const trialModel = getStagingImageModel();
    const waiveCreditsForTrial =
      (import.meta.env.DEV || isAdmin) && trialModel !== 'gpt-sunburst';

    if (mustChargeNewBundle) {
        const confirmBundle = window.confirm(
          waiveCreditsForTrial
            ? `Trial (${trialModel}) — no credit charge. Includes ${GENERATIONS_PER_BUNDLE} attempts on this photo.`
            : renovation
            ? `Full renovation — ${stagingCost} credits. Includes ${GENERATIONS_PER_BUNDLE} attempts on this photo.`
            : `Generate — ${stagingCost} credits. Includes ${GENERATIONS_PER_BUNDLE} staging attempts on this photo.`
        );
        if (!confirmBundle) return;

        if (!waiveCreditsForTrial) {
          setIsConsumingCredit(true);
          const result = await consumeCredits(stagingCost);
          setIsConsumingCredit(false);
          if (result !== 'success') {
              if (result === 'insufficient_credits') {
                setShowBuyCreditsModal(true);
              } else {
                setError('Could not take credits for this generation. Please refresh and try again.');
                window.alert('Could not take credits for this generation. Please refresh the page and try again.');
              }
              return;
          }
          chargedThisRequest = true;
        } else {
          chargedThisRequest = false;
        }
        availableGenerations = GENERATIONS_PER_BUNDLE;
    }

    setAppState(AppState.PROCESSING);
    setError(null);
    setStructureWarning(null);
    suppressAuthSyncRef.current = true;

    const jobId = crypto.randomUUID();

    // Create generation record
    const generationId = await createGenerationRecord(
        session?.user?.id,
        userProfile?.email,
        { action: renovation ? 'full_renovation' : 'manual', job_id: jobId, prompt: prompt?.substring(0, 100), source_app: 'staging', ai_operation: renovation ? 'full_renovation' : 'virtual_staging' }
    );

    // When the user clicks "Request changes" on a staged 360, the previous
    // staged image becomes the source we want to anchor against (polar restore,
    // tint match, GPano XMP). Consume the one-shot override here so subsequent
    // calls fall back to the original upload.
    const restorationSource: File | null = refinementSourceRef.current ?? images.originalFile;
    refinementSourceRef.current = null;

    let inputUrl: string | undefined;
    try {
      if (session?.user && images.originalFile) {
          // IMPORTANT: Insert record BEFORE upload so trigger doesn't create with wrong service_type
          console.log('STAGING: Pre-inserting with service_type: staging', { jobId });
          await supabase.from('images').upsert({
                  id: jobId,
                  user_id: session.user.id,
                  input_url: '',
                  output_url: '',
                  status: 'processing',
                  type: 'restoration',
                  service_type: 'staging'
          });

          // Input upload is bookkeeping only — AI uses the local canvas bytes.
          // Don't abort the whole job if storage is flaky after a token refresh.
          try {
              inputUrl = await uploadImageToStorage(session.user.id, images.originalFile, 'original', jobId);
              if (inputUrl) {
                  await supabase.from('images').update({
                      input_url: inputUrl
                  }).eq('id', jobId);
              }
          } catch (uploadErr) {
              console.warn('STAGING: input upload failed; continuing with local image', uploadErr);
          }
      }

      // Show warning for 360 images
      if (is360) {
        console.warn('Processing 360 equirectangular image - aspect ratio preserved');
      }
      
      // Client-side Gemini processing (with optional reference for "match this image")
      let resultBase64: string;
      if (roomAnchorUrlParam) {
        // Same-room anchor: keep the staged hero at high resolution so Gemini
        // can read the actual furniture LAYOUT (not just the style) and
        // reproduce the same pieces from this new angle. Inventory checklist
        // (when available) makes piece identity explicit in the prompt.
        const anchorBase64 = await prepareAnchorForGemini(roomAnchorUrlParam);
        resultBase64 = await declutterImageWithRoomAnchor(
          maskedImageData,
          anchorBase64,
          prompt,
          aspectRatio,
          sameRoomInventory,
        );
      } else if (referenceImageUrlParam) {
        // Reference is downscaled to a small thumbnail so Gemini treats
        // it as a style swatch, not a scene it can pixel-transplant onto
        // the target. See `prepareReferenceForGemini` for the rationale.
        const referenceBase64 = await prepareReferenceForGemini(referenceImageUrlParam);
        resultBase64 = await declutterImageWithReference(maskedImageData, referenceBase64, prompt, aspectRatio);
      } else if (maskImageData && isFixOpeningsPrompt(prompt) && images.originalFile) {
        // Brush repair: restore openings from the original unfinished shell
        // inside the painted region only (Continue Editing on a bad renovation).
        const shellPrepared = await prepareImageForAPI(images.originalFile);
        resultBase64 = await repairStructureRegion(
          maskedImageData,
          maskImageData,
          shellPrepared.imageData,
          prompt,
          aspectRatio,
        );
      } else if (maskImageData) {
        resultBase64 = await editImageWithMask(maskedImageData, maskImageData, prompt, aspectRatio);
      } else {
        resultBase64 = await declutterImage(maskedImageData, prompt, aspectRatio);
      }
      let croppedResult = await cropResultToOriginal(resultBase64, restoreRect, is360);

      // Ceiling-band paste-back is disabled (CEILING_LOCK_FRACTION = 0). It
      // left a visible strip of the original ceiling on staging and renovation.
      if (
        restorationSource &&
        !is360 &&
        !maskImageData &&
        !isRecolorPrompt(prompt) &&
        shouldLockCeiling(prompt) &&
        creditCostOverride !== STAGING_FULL_RENOVATION_CREDITS &&
        CEILING_LOCK_FRACTION > 0
      ) {
        try {
          const originalSourceDataUrl = await fileToDataUrl(restorationSource);
          croppedResult = await compositeTopBand(
            originalSourceDataUrl,
            croppedResult,
            CEILING_LOCK_FRACTION
          );
        } catch (lockErr) {
          console.warn('Ceiling-band lock failed, using raw result:', lockErr);
        }
      }

      // Mask-aware composite: when the user used the brush + a mask-aware
      // flow (recolour or removal), restore everything OUTSIDE the brushed
      // area to the image the user was actually editing. This guarantees no
      // red mask residue, no global colour cast outside the brush, and no
      // accidental edits (walls / perspective / other staging) beyond the
      // user's selection.
      //
      // The composite anchor is `maskedImageData` (the CLEAN source that was
      // sent to Gemini), NOT the original upload: when the user is iterating
      // on an already-staged result via "Continue Editing", the working image
      // is the staged photo — anchoring to the first upload here would wipe
      // the staging outside the brush. The mask flow always passes the clean
      // source in `maskedImageData`, so it aligns pixel-for-pixel.
      if (maskImageData) {
        try {
          const workingSourceDataUrl = await cropResultToOriginal(maskedImageData, restoreRect, is360);
          const maskAtOriginalDims = await cropResultToOriginal(maskImageData, restoreRect, is360);
          croppedResult = await compositeMaskedResult(
            workingSourceDataUrl,
            croppedResult,
            maskAtOriginalDims
          );
        } catch (blendErr) {
          console.warn('Mask-aware composite failed, using raw Gemini output:', blendErr);
        }
      }

      // Cancel any global colour cast Gemini may have introduced. Skipped for
      // explicit recolour prompts (the user wants a colour shift in those
      // flows). Anchored to the restoration source's white point so iterative
      // passes stop compounding the drift. For a refinement pass, the
      // restoration source is the previously-staged image — that's the
      // intended white-point anchor for "request changes" iterations.
      // Skipped for mask flows (recolour/removal): the mask-aware composite
      // above already restores everything outside the brush to the working
      // image, so a global tint pass — anchored to the first upload — would
      // wrongly recolour an already-correct (possibly staged) image.
      if (restorationSource && !isRecolorPrompt(prompt) && !maskImageData && !renovation) {
          try {
              const originalSourceDataUrl = await fileToDataUrl(restorationSource);
              const tintFix = await removeGlobalTint(originalSourceDataUrl, croppedResult);
              if (tintFix.method !== 'none') {
                  console.log(
                      `Tint fix (manual, ${tintFix.method}): anchors=${tintFix.anchorCount} ` +
                      `gains=R${tintFix.gains.r.toFixed(3)} ` +
                      `G${tintFix.gains.g.toFixed(3)} ` +
                      `B${tintFix.gains.b.toFixed(3)}`
                  );
              }
              croppedResult = tintFix.dataUrl;
          } catch (tintErr) {
              console.warn('Tint removal (manual) failed:', tintErr);
          }
      }

      // For 360 panoramas: hard-restore polar bands and re-inject GPano XMP.
      // Exception: when the user asked to repair the nadir (tripod removal),
      // skip the bottom restore — otherwise we'd undo the very edit Gemini just made.
      const isTripodRemoval = /remove the tripod/i.test(prompt);
      if (is360) {
          if (restorationSource) {
              try {
                  const sourceDataUrl = await fileToDataUrl(restorationSource);

                  // Resolution recovery: Gemini caps 360 output at 4K, but a
                  // 360 camera shoots 8K+. Since staging only ADDS furniture to
                  // an otherwise-preserved room, paste the full-resolution
                  // original back everywhere the model didn't change, keeping
                  // only the staged furniture/decor from Gemini's 4K output.
                  // Skipped for recolour prompts (those intentionally want a
                  // global colour change that the composite would revert).
                  if (!isRecolorPrompt(prompt) && !renovation) {
                      try {
                          croppedResult = await compositeStagedOverOriginal(
                              sourceDataUrl,
                              croppedResult
                          );
                      } catch (compositeErr) {
                          console.warn('Full-res 360 composite failed, using Gemini output:', compositeErr);
                      }
                  }

                  if (!renovation) {
                      croppedResult = await restorePolarRegions(
                          croppedResult,
                          sourceDataUrl,
                          isTripodRemoval
                              ? { bottomFraction: 0, blendFraction: 0, outputType: 'image/jpeg' }
                              : { outputType: 'image/jpeg' }
                      );
                  }
              } catch (polarErr) {
                  console.warn('Polar restore (manual) failed:', polarErr);
              }
          }
          try {
              const dims = await getImageDimensions(croppedResult);
              const gpanoSource = restorationSource
                  ? await readGPanoXmp(restorationSource).catch(() => null)
                  : null;
              const meta = resolveGPanoMeta(dims.width, dims.height, gpanoSource);
              croppedResult = dataUrlWithGPano(croppedResult, meta);
          } catch (xmpErr) {
              console.warn('GPano XMP injection (manual) failed:', xmpErr);
          }
      }

      let outputUrl: string | undefined;
      if (session?.user) {
           try {
             outputUrl = await uploadImageToStorage(session.user.id, croppedResult, 'processed', jobId);
             if (outputUrl) {
                  await supabase.from('images').update({
                      output_url: outputUrl,
                      status: 'completed',
                      type: 'restoration'
                  }).eq('id', jobId);
             }
           } catch (uploadErr) {
             console.warn('STAGING: output upload failed; keeping local result', uploadErr);
           }
      }

      if (!croppedResult) {
          throw new Error('Processing returned empty result');
      }
      
      // Record bundle charge only on the generation that triggered payment.
      if (generationId) {
          await updateGenerationRecord(
            generationId,
            'completed',
            chargedThisRequest ? stagingCost : 0,
            undefined,
            outputUrl,
            inputUrl
          );
      }

      try {
        if (generationId) {
          await persistAiProvenance({
            supabase,
            target: 'generations',
            id: generationId,
            app: 'staging',
            operation: 'virtual_staging',
            sourceAssetId: inputUrl || jobId,
            outputAssetId: outputUrl || jobId,
            provider: 'google',
            model: 'gemini-3-pro-image-preview',
          });
        }
        if (session?.user) {
          await persistAiProvenance({
            supabase,
            target: 'images',
            id: jobId,
            app: 'staging',
            operation: 'virtual_staging',
            sourceAssetId: inputUrl || jobId,
            outputAssetId: outputUrl || jobId,
            provider: 'google',
            model: 'gemini-3-pro-image-preview',
          });
        }
      } catch (provenanceErr) {
        console.warn('AI provenance persist (manual) failed:', provenanceErr);
      }

      setGenerationBundleKind(requestedBundleKind);
      setBundleGenerationsRemaining(Math.max(availableGenerations - 1, 0));
      setLastOutputUrl(outputUrl ?? null);
      
      setImages(prev => ({
          ...prev,
          maskDataUrl: maskedImageData,
          processedUrl: croppedResult
      }));

      // Surface opening/wall failures after structure retries (staging + renovation).
      // Fix-openings brush repairs skip the inspector and clear the banner.
      if (isFixOpeningsPrompt(prompt)) {
        setStructureWarning(null);
      } else {
        const structureVerdict = getLastStructureVerdict();
        if (!structureVerdict.ok && structureVerdict.violations.length > 0) {
          const detail = structureVerdict.violations.slice(0, 2).join(' ');
          setStructureWarning(
            renovation
              ? `The AI changed openings or walls (${detail}). Regenerate, or Continue Editing and brush over the wrong door/wall, then use Fix openings.`
              : `The AI changed the room structure (${detail}). Go back and generate again — this photo still has attempts left in the bundle.`,
          );
        } else {
          setStructureWarning(null);
        }
      }

      if (sameRoomTotal > 0) {
        // Same-room batch. Only the HERO establishes/refreshes the anchor +
        // inventory. Follow-ups keep the existing hero. Completed indices are
        // unique (regenerations do not inflate the "all done" counter).
        const onHero = !roomAnchorUrlParam && sameRoomCurrentIndex === 0;
        const angleIndex = sameRoomCurrentIndex;

        if (onHero) {
          // ANCHOR GUARD: a hero that failed the structural inspection is a
          // re-render of a different room — using it as the anchor poisons
          // every follow-up angle. Require a clean hero before continuing.
          const structureVerdict = getLastStructureVerdict();
          if (!structureVerdict.ok) {
            setSameRoomAnchorUrl(null);
            setSameRoomInventory(null);
            setSameRoomAnchorWarning(
              'This result changed the room\u2019s structure, so it can\u2019t anchor the other angles. Go back and stage this photo again before continuing.'
            );
          } else {
            setSameRoomAnchorWarning(null);
            setSameRoomCompletedIndices((prev) =>
              prev.includes(angleIndex) ? prev : [...prev, angleIndex],
            );
            setSameRoomAnchorUrl(croppedResult);
            // Extract furniture inventory before the user advances — follow-ups
            // use this checklist plus the high-res anchor.
            try {
              const inventory = await extractFurnitureInventory(croppedResult);
              if (inventory) setSameRoomInventory(inventory);
            } catch (e) {
              console.warn('Furniture inventory extract failed after hero stage:', e);
            }
          }
        } else {
          // Follow-up angle (or hero regenerated via the anchored path):
          // always counts toward batch completion.
          setSameRoomCompletedIndices((prev) =>
            prev.includes(angleIndex) ? prev : [...prev, angleIndex],
          );
          if (roomAnchorUrlParam && angleQueue.length > 0) {
            // Auto-advance to the next queued angle after a short beat so the
            // user can glance at the result, then continue the batch.
            if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = setTimeout(() => {
              autoAdvanceTimerRef.current = null;
              handleStageNextAngle();
            }, 1600);
          }
        }
      } else if (isMultiAngleStagingPrompt(prompt, !!referenceImageUrlParam)) {
        setReferenceImageUrl(croppedResult);
      }

      // First staged result this browser has ever seen → surface the
      // "not happy? regenerate, you have N passes" notice once.
      try {
        if (!localStorage.getItem('staging_passes_notice_dismissed')) {
          setShowPassesNotice(true);
        }
      } catch {
        setShowPassesNotice(true);
      }

      setLastStagingImageId(jobId);
      setAppState(AppState.RESULT);
      setIsConsumingCredit(false);
    } catch (err: any) {
      console.error("Process failed:", err);
      
      // Failed generation records should not show credits used.
      if (generationId) {
          await updateGenerationRecord(generationId, 'failed', 0, err?.message);
      }
      
      const errorMsg = err?.message || String(err) || '';

      // Refund only if this request charged a new bundle.
      if (session?.user) {
          if (chargedThisRequest) {
            for (let i = 0; i < stagingCost; i++) {
                await refundCredit(session.user.id);
            }
            setUserProfile(prev => prev ? { ...prev, credits: prev.credits + stagingCost } : null);
          }
          await supabase.from('images').update({ status: 'failed' }).eq('id', jobId);
      }
      
      setIsConsumingCredit(false);

      setError(chargedThisRequest
        ? "Processing failed. Your credits have been refunded. Please try again."
        : "Processing failed. Please try again.");
      setAppState(AppState.EDITOR);
    } finally {
      suppressAuthSyncRef.current = false;
    }
  };

  const handleUpscale = async () => {
    if (!images.processedUrl) return;
    
    setIsUpscaling(true);
    try {
      const upscaledBase64 = await upscaleImage(images.processedUrl);
      
      setImages(prev => ({
        ...prev,
        processedUrl: upscaledBase64
      }));
    } catch (err: any) {
      console.error(err);
      alert("Upscaling failed. Please try again.");
    } finally {
      setIsUpscaling(false);
    }
  };

  const handleAccept = (imageUrlToUse?: string) => {
    // imageUrlToUse is the curves-adjusted image when ResultViewer has one;
    // otherwise carry forward the raw staged result.
    const url = imageUrlToUse ?? images.processedUrl;
    if (url) {
      setImages(prev => ({
        ...prev,
        originalUrl: url,
        processedUrl: null,
        maskDataUrl: null,
        // is360 carries forward — accepting a 360 result keeps panorama mode.
      }));
      setAppState(AppState.EDITOR);
      setIsUpscaling(false);
    }
  };

  /**
   * "Request changes" refinement on the current 360 staged result.
   *
   * Treats the currently-displayed staged panorama as the new input, runs
   * the same Gemini pipeline as a fresh stage with REFINE_STAGED_360_PROMPT
   * (templated with the user's free-text change request), and surfaces the
   * refined image as the new processedUrl. The user can chain multiple
   * refinements without going back to the editor.
   *
   * Source-anchoring: the previously-staged image is loaded as a File and
   * stashed in `refinementSourceRef` so handleProcess uses IT for polar
   * restore + tint anchor + GPano source — not the original empty room.
   * That's what keeps the staged decor on the floor / poles consistent
   * across iterations.
   *
   * Cost: counts as one generation from the current bundle, same as a
   * fresh stage. If the bundle is exhausted, handleProcess prompts the
   * user to start a new bundle (existing behaviour).
   *
   * Throws on failure so ResultViewer can show an inline error.
   */
  const handleRequestChanges = async (userChangesText: string): Promise<void> => {
    const text = (userChangesText ?? '').trim();
    if (!text) throw new Error('Please describe the change you want.');
    if (!images.processedUrl) throw new Error('No staged image to refine.');
    if (isConsumingCredit) throw new Error('Another operation is in progress.');

    // Fetch the current staged result as a File so the existing Gemini
    // pipeline (which expects a File-shaped source) can use it both as
    // input AND as the restoration anchor.
    const stagedRes = await fetch(images.processedUrl);
    if (!stagedRes.ok) {
      throw new Error(`Could not load the staged image (HTTP ${stagedRes.status}).`);
    }
    const stagedBlob = await stagedRes.blob();
    const stagedExt = stagedBlob.type === 'image/jpeg' ? 'jpg' : 'png';
    const stagedFile = new File([stagedBlob], `staged-refine.${stagedExt}`, {
      type: stagedBlob.type || 'image/png',
    });

    // Pad to 21:9 with the same edge-replicate logic the standard upload
    // path uses for 360 panos. Reusing prepareImageForAPI keeps the
    // restoreRect math in one place.
    const prepared = await prepareImageForAPI(stagedFile);
    if (!prepared.is360) {
      throw new Error('Request-changes only works on 360° panoramas.');
    }

    refinementSourceRef.current = stagedFile;

    const prompt = REFINE_STAGED_360_PROMPT.replace('{{USER_CHANGES}}', text);

    try {
      await handleProcess(
        prepared.imageData,
        prompt,
        prepared.aspectRatio,
        prepared.restoreRect,
        true,
      );
    } catch (err) {
      // handleProcess swallows errors into setError + UI state, but if it
      // throws synchronously we still want to clear the override.
      refinementSourceRef.current = null;
      throw err;
    }

    // If handleProcess transitioned to RESULT successfully, processedUrl
    // is the refined image. If it left an `error` set instead, surface
    // that as a thrown error so the panel shows it inline.
    if (error) throw new Error(error);
  };

  const handleReset = () => {
    if (images.originalUrl) URL.revokeObjectURL(images.originalUrl);
    setImages({
      originalFile: null,
      originalUrl: null,
      processedUrl: null,
      maskDataUrl: null,
      is360: false,
    });
    setReferenceImageUrl(null);
    clearSameRoomBatch();
    setAppState(AppState.UPLOAD);
    setError(null);
    setIsUpscaling(false);
    setBundleGenerationsRemaining(0);
  };

  const downloadCurrentOriginal = () => {
    if (!images.originalUrl) return;
    const link = document.createElement('a');
    link.href = images.originalUrl;
    link.download = `Virtualstagingstudio-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendToPhotoplan = async (imageUrl: string) => {
    if (!photoplanReturn) return;
    let url = imageUrl;
    if (!/^https:\/\//i.test(url) && session?.user) {
      const uploaded = await uploadImageToStorage(session.user.id, url, 'output');
      if (!uploaded) {
        alert('Could not save the result to send back to Photoplan.');
        return;
      }
      url = uploaded;
    }
    if (!/^https:\/\//i.test(url)) {
      alert('Could not send this result back to Photoplan. Try again after processing finishes.');
      return;
    }
    sendBackToPhotoplan(photoplanReturn, url);
  };

  const handleSendToHyperdusk = async (imageUrl: string) => {
    const params = new URLSearchParams({ imageUrl, source: 'staging' });
    if (photoplanReturn) {
      params.set('returnTo', 'photoplan');
      params.set('returnOrigin', photoplanReturn.returnOrigin);
      params.set('bookingId', photoplanReturn.bookingId);
      params.set('assetId', photoplanReturn.assetId);
    }
    let hash = '';
    if (isConfigured) {
      try {
        const { data: { session: activeSession } } = await supabase.auth.getSession();
        if (activeSession?.access_token && activeSession?.refresh_token) {
          hash = `#sso_access_token=${encodeURIComponent(activeSession.access_token)}&sso_refresh_token=${encodeURIComponent(activeSession.refresh_token)}`;
        }
      } catch (_) {}
    }
    window.open(`https://hyperdusk.rehub.studio/?${params.toString()}${hash}`, '_blank', 'noopener,noreferrer');
  };

  const handleAnimateStaging = async (imageId: string) => {
    await logEvent('virtual_staging_animate_clicked', session?.user?.id ?? null, session?.user?.email ?? null, null, null, {
      source: 'virtual_staging',
      source_image_id: imageId,
    });
    const params = new URLSearchParams({
      sourceImage: imageId,
      source: 'virtual_staging',
    });
    let hash = '';
    if (isConfigured) {
      try {
        const { data: { session: activeSession } } = await supabase.auth.getSession();
        if (activeSession?.access_token && activeSession?.refresh_token) {
          hash = `#sso_access_token=${encodeURIComponent(activeSession.access_token)}&sso_refresh_token=${encodeURIComponent(activeSession.refresh_token)}`;
        }
      } catch (_) {}
    }
    window.open(`https://imagemotion.rehub.studio/staging-animation?${params.toString()}${hash}`, '_blank', 'noopener,noreferrer');
  };

  const handleSendTo360Tours = async (imageUrl: string) => {
    // Branch A: this staging session was opened by Tours' in-tour
    // "Stage 360°" button. URL carries returnTo/tourId/sceneId — send
    // the result back to that specific scene rather than starting a new
    // tour from scratch.
    const returnCtx = getToursReturnContext();
    if (returnCtx) {
      try {
        // Persist a staged_returns row first so the closed-tab fallback
        // in tours can pick it up even if window.opener is gone.
        let stagedReturnId: string | undefined;
        if (isConfigured && session?.user?.id) {
            try {
                            const { data: insertData, error: insertErr } = await supabase
              .from('staged_returns')
              .insert({
                user_id: session.user.id,
                tour_id: returnCtx.tourId,
                scene_id: returnCtx.sceneId,
                staged_image_url: imageUrl,
                variant_label: 'Staged',
                staged_meta: {
                  version: 1,
                  source: 'staging-app',
                  is_360: images.is360 ?? true,
                  label: 'Staged',
                },
              })
              .select('id')
              .single();
            if (!insertErr && insertData?.id) {
              stagedReturnId = insertData.id as string;
            } else if (insertErr) {
              console.warn('[handleSendTo360Tours] staged_returns insert failed:', insertErr.message);
            }
          } catch (insertErrCatch) {
            console.warn('[handleSendTo360Tours] staged_returns insert threw:', insertErrCatch);
          }
        }

        // Live-tab return path: postMessage the opener so the result
        // applies immediately if the Tours tab is still open. Wrapped
        // in try because postMessage to a cross-origin opener can throw
        // when the opener is gone.
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              {
                type: 'staging-complete',
                tourId: returnCtx.tourId,
                sceneId: returnCtx.sceneId,
                stagedImageUrl: imageUrl,
                variantLabel: 'Staged',
                stagedMeta: {
                  version: 1,
                  source: 'staging-app',
                  is_360: images.is360 ?? true,
                  label: 'Staged',
                },
                stagedReturnId,
              },
              returnCtx.returnOrigin,
            );
          }
        } catch (postErr) {
          console.warn('[handleSendTo360Tours] postMessage failed (staged_returns fallback will pick this up):', postErr);
        }

        // Friendly confirmation, then close the popup. If close is blocked
        // (e.g. the user navigated within this window after opening it),
        // show the toast as a longer-lived banner.
        const closed = (() => {
          try {
            window.close();
            return window.closed;
          } catch {
            return false;
          }
        })();
        if (!closed) {
          window.alert('Sent the staged panorama back to your 360 Tour. You can close this tab.');
        }
      } catch (err) {
        console.error('[handleSendTo360Tours] return-to-scene flow failed:', err);
        window.alert('Could not send the result back automatically. The image is saved in your gallery; please open the tour and re-attach it manually.');
      }
      return;
    }

    // Branch B: standalone staging session — pop the SendToTourModal
    // picker so the user can pick a tour + scene to attach this staged
    // variant to. Replaces the old "open the tours dashboard with
    // ?imageUrl=" flow which dumped the user with no context and no
    // path to actually use the staged result.
    setSendToTourImageUrl(imageUrl);
  };

  const handleDownload = async (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null, sourceUrl?: string, aiDisclaimer?: boolean) => {
    // sourceUrl is passed by ResultViewer when the user applied curves; fall
    // back to the raw staged result otherwise.
    const processedUrl = sourceUrl ?? images.processedUrl;
    
    if (!processedUrl) {
      console.error('No processed image URL available for download');
      return;
    }
    
    console.log('Starting download:', { format, hasWatermark: !!watermark, aiDisclaimer: !!aiDisclaimer, urlType: processedUrl.startsWith('data:') ? 'base64' : 'remote' });
    
    // Helper to trigger download from a data URL or blob URL
    const triggerDownload = (url: string, filename: string) => {
      console.log('Triggering download...');
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Small delay before removing to ensure download starts
      setTimeout(() => document.body.removeChild(link), 100);
    };

    // Resolve GPano metadata once (only used when the result is a 360 panorama).
    const is360Result = !!images.is360;
    let cachedGpano: GPanoMeta | null = null;
    const getGpanoMeta = async (): Promise<GPanoMeta | null> => {
      if (!is360Result) return null;
      if (cachedGpano) return cachedGpano;
      try {
        const dims = await getImageDimensions(processedUrl);
        const gpanoSource = images.originalFile
          ? await readGPanoXmp(images.originalFile).catch(() => null)
          : null;
        cachedGpano = resolveGPanoMeta(dims.width, dims.height, gpanoSource);
        return cachedGpano;
      } catch (err) {
        console.warn('Resolving GPano metadata for download failed:', err);
        return null;
      }
    };

    /**
     * Like triggerDownload, but for 360° outputs we re-inject GPano XMP into
     * the final PNG/JPEG bytes so platforms still recognise the file as a
     * panorama. Falls back to a normal download on any error.
     */
    const triggerDownloadWithMeta = async (url: string, filename: string) => {
      if (!is360Result) {
        triggerDownload(url, filename);
        return;
      }
      try {
        const meta = await getGpanoMeta();
        if (!meta) {
          triggerDownload(url, filename);
          return;
        }
        if (url.startsWith('data:')) {
          const finalUrl = dataUrlWithGPano(url, meta);
          triggerDownload(finalUrl, filename);
          return;
        }
        // blob: or remote URL — fetch, inject, re-blob.
        const res = await fetch(url);
        const blob = await res.blob();
        const stamped = await blobWithGPano(blob, meta);
        const blobUrl = URL.createObjectURL(stamped);
        triggerDownload(blobUrl, filename);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        console.warn('GPano injection on download failed, downloading raw file:', err);
        triggerDownload(url, filename);
      }
    };
    
    const filename = `Virtualstagingstudio-${Date.now()}.${format}`;
    
    // Quick path: If it's base64, no watermark/disclaimer, and format matches - download directly
    if (processedUrl.startsWith('data:') && !watermark && !aiDisclaimer) {
      const isJpeg = processedUrl.includes('image/jpeg') || processedUrl.includes('image/jpg');
      const isPng = processedUrl.includes('image/png');
      
      if ((format === 'jpeg' && isJpeg) || (format === 'png' && isPng)) {
        console.log('Direct base64 download (format matches)');
        triggerDownloadWithMeta(processedUrl, filename);
        return;
      }
    }
    
    // For all other cases, use canvas to process/convert the image
    const img = new Image();
    
    // Only set crossOrigin for remote URLs, not for data URLs
    if (!processedUrl.startsWith('data:')) {
      img.crossOrigin = "anonymous";
    }
    
    img.onload = () => {
      console.log('Image loaded for processing:', img.width, 'x', img.height);
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Failed to get canvas context');
        // Fallback: try direct download
        triggerDownload(processedUrl, filename);
        return;
      }
      
      ctx.drawImage(img, 0, 0);

      const finishDownload = () => {
        if (aiDisclaimer) {
          drawAiDisclaimer(ctx, img.width, img.height, getDownloadLabel('AI_MODIFIED'));
        }
        try {
          const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
          const quality = format === 'jpeg' ? 0.95 : undefined;
          const dataUrl = canvas.toDataURL(mimeType, quality);
          console.log('Canvas converted, downloading...');
          triggerDownloadWithMeta(dataUrl, filename);
        } catch (e) {
          console.error('Canvas toDataURL failed:', e);
          // Fallback: try direct download
          triggerDownload(processedUrl, filename);
        }
      };

      if (watermark) {
        const logo = new Image();
        logo.onload = () => {
          const logoWidth = img.width * watermark.scale;
          const logoHeight = logoWidth * (logo.height / logo.width);
          const logoX = img.width * watermark.x;
          const logoY = img.height * watermark.y;
          ctx.globalAlpha = watermark.opacity;
          ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
          ctx.globalAlpha = 1.0;
          finishDownload();
        };
        logo.onerror = () => {
          console.warn('Watermark logo failed to load, downloading without watermark');
          finishDownload();
        };
        logo.src = watermark.imageUrl;
      } else {
        finishDownload();
      }
    };
    
    img.onerror = (e) => {
      console.error('Image failed to load for download:', e);
      
      // Fallback: try fetch + blob approach for remote URLs
      if (!processedUrl.startsWith('data:')) {
        console.log('Trying fetch fallback...');
        fetch(processedUrl)
          .then(response => response.blob())
          .then(async blob => {
            // For 360 panoramas, restamp GPano XMP onto the raw blob.
            let outBlob = blob;
            if (is360Result) {
              try {
                const meta = await getGpanoMeta();
                if (meta) outBlob = await blobWithGPano(blob, meta);
              } catch (xmpErr) {
                console.warn('GPano XMP injection on fetch fallback failed:', xmpErr);
              }
            }
            const blobUrl = URL.createObjectURL(outBlob);
            triggerDownload(blobUrl, filename);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          })
          .catch(fetchError => {
            console.error('Fetch download also failed:', fetchError);
            // Last resort: open in new tab
            window.open(processedUrl, '_blank');
          });
      } else {
        // For data URLs that somehow failed, try direct download anyway
        triggerDownload(processedUrl, filename);
      }
    };
    
    img.src = processedUrl;
  };

  // Public share demo page — no auth required
  const shareMatch = currentPath.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
  if (shareMatch) {
    return <ShareDemoPage slug={shareMatch[1]} />;
  }

  // Embeddable page routes - render without main app shell
  const isEmbedRoute = currentPath === '/embed' || currentPath === '/pricing' || currentPath === '/signup';
  if (isEmbedRoute) {
     const isIframe = window.self !== window.top;
     return <EmbedPage isEmbed={isIframe} />;
  }

  if (currentPath === '/login') {
     return <Login />;
  }

  const orderDetailMatch = currentPath.match(/^\/orders\/([0-9a-f-]{36})$/i);
  if (currentPath === '/orders' || orderDetailMatch) {
    if (authLoading) {
      return (
        <div className="flex items-center justify-center h-screen bg-black">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
      );
    }
    if (!session?.user) {
      return <Login />;
    }
    if (orderDetailMatch) {
      return (
        <div className="h-screen overflow-y-auto bg-black">
          <CommerceOrderDetail orderId={orderDetailMatch[1]} userId={session.user.id} />
        </div>
      );
    }
    return (
      <div className="h-screen overflow-y-auto bg-black">
        <CommerceOrdersList userId={session.user.id} />
      </div>
    );
  }

  if (currentPath === '/services') {
    if (authLoading) {
      return (
        <div className="flex items-center justify-center h-screen bg-black">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
      );
    }
    if (!session?.user) {
      return <Login />;
    }
    return (
      <div className="h-screen overflow-y-auto bg-black">
        <ServicesWizard
          userId={session.user.id}
          userCredits={userProfile?.credits ?? 0}
        />
      </div>
    );
  }

  if (authLoading && appState === AppState.INITIALIZING) {
    return (
      <div className="flex items-center justify-center h-screen bg-black flex-col gap-4">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        <p className="text-slate-400 animate-pulse">Initializing Application...</p>
      </div>
    );
  }

  // Landing page (features + sign up / login) when not authenticated at root — like 360tours
  if (!session && currentPath === '/') {
    return (
      <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
        <FluidBackground />
        <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <Header
            onReset={() => {}}
            showNewPhoto={false}
            user={null}
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
            onOpenDashboard={() => { setAuthMode('login'); setShowLoginModal(true); }}
            isAdmin={isAdmin}
            onOpenAdmin={() => setShowAdminPanel(true)}
          />
          <LandingPage
            onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onOpenPricing={() => setShowPricingBenefitsModal(true)}
          />
          <div className="shrink-0">
            <Footer />
          </div>
          {showPricingBenefitsModal && (
            <PricingBenefitsModal
              onClose={() => setShowPricingBenefitsModal(false)}
              onSignUp={() => { setShowPricingBenefitsModal(false); setAuthMode('signup'); setShowLoginModal(true); }}
            />
          )}
          {showLoginModal && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="w-full max-w-lg">
                <Login
                  onClose={() => setShowLoginModal(false)}
                  initialMode={authMode}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Cross-app handoff should not depend on storage upload success.
  // If `lastOutputUrl` is missing (e.g. transient upload failure), fall
  // back to the in-memory processed data URL so users can still return
  // the staged pano to Tours.
  const resultHandoffImageUrl = lastOutputUrl || images.processedUrl || '';

  return (
    <div className="h-full flex flex-col bg-black min-h-screen">
      {isImportingFromExternal && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-slate-900/90 backdrop-blur-md border border-purple-500/40 text-slate-200 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm animate-in fade-in slide-in-from-top-4 duration-200">
          <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
          <span>Importing image from Photoclear…</span>
        </div>
      )}
<Header
        onReset={handleReset}
        showNewPhoto={appState !== AppState.UPLOAD && appState !== AppState.INITIALIZING}
        user={userProfile}
        onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
        onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
        onOpenDashboard={handleOpenDashboard}
        onOpenBuyCredits={() => setShowBuyCreditsModal(true)}
        onNavigateToSettings={handleOpenDashboard}
        onNavigateToMembers={handleOpenDashboard}
        onNavigateToReferrals={() => setShowReferrals(true)}
        isAdmin={isAdmin}
        onOpenAdmin={() => setShowAdminPanel(true)}
        onLogout={handleLogout}
      />
      {userProfile && (
        <div className="px-4 pt-3">
          <LowCreditsBanner
            balance={userProfile.credits ?? 0}
            needed={creditsForStagingTier(selectedStagingTier)}
            onBuy={() => setShowBuyCreditsModal(true)}
          />
        </div>
      )}
      {/* Scrollable column: main content + footer so footer sits below upload panel (scroll to see it) */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
      <main className="flex-1 min-h-[calc(100vh-4rem)] overflow-hidden relative">
        {appState === AppState.INITIALIZING && (
           <div className="flex items-center justify-center h-full">
             <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
           </div>
        )}

        {appState === AppState.UPLOAD && (
          <>
            <FluidBackground />
            <div className="relative z-10 flex-1 flex min-h-full items-center justify-center w-full">
              <div className="w-full max-w-4xl mx-auto">
                <UploadZone 
                  onImageSelected={handleImageSelected} 
                  onImagesSelected={handleImagesSelected}
                  referenceImageUrl={referenceImageUrl}
                  onClearReference={() => setReferenceImageUrl(null)}
                  sameRoomAnchorUrl={
                    sameRoomAnchorUrl &&
                    !referenceImageUrl &&
                    angleQueue.length === 0 &&
                    sameRoomCompletedIndices.length > 0 &&
                    sameRoomCompletedIndices.length < sameRoomTotal
                      ? sameRoomAnchorUrl
                      : null
                  }
                  onClearSameRoomAnchor={clearSameRoomBatch}
                />
              </div>
            </div>
          </>
        )}

        {appState === AppState.PROCESSING && images.originalUrl && !images.maskDataUrl ? (
            <div className="flex flex-col items-center justify-center h-full gap-8 bg-black animate-in fade-in duration-500">
                 <div className="relative">
                     <div className="absolute inset-0 bg-purple-500/20 blur-xl rounded-full animate-pulse"></div>
                     <Loader2 className="w-16 h-16 text-purple-500 animate-spin relative z-10" />
                 </div>
                 <div className="text-center space-y-2">
                     <h2 className="text-2xl font-bold text-slate-100">
                       {processingKind === 'renovate' ? 'Renovating Your Room' : 'Staging Your Room'}
                     </h2>
                     <p className="text-slate-400 max-w-sm">
                         {processingKind === 'renovate'
                           ? 'Finishing the build, checking openings stay put, then staging. This can take up to a minute if the AI needs a retry.'
                           : 'Our AI is analyzing the space and adding virtual furniture. This usually takes 10-20 seconds.'}
                     </p>
                     {(import.meta.env.DEV || isAdmin) && (
                       <p className="text-[11px] text-amber-400/80 font-medium tracking-wide">
                         Model: {getStagingImageModel()}
                       </p>
                     )}
                 </div>
            </div>
        ) : (
            (appState === AppState.EDITOR || appState === AppState.PROCESSING) && images.originalUrl && (
              <>
                <Editor 
                  imageUrl={images.originalUrl} 
                  onProcess={handleProcess}
                  isProcessing={appState === AppState.PROCESSING}
                  onDownload={downloadCurrentOriginal}
                  onOpenDashboard={handleOpenDashboard}
                  onForwardToResult={images.processedUrl && appState === AppState.EDITOR ? () => setAppState(AppState.RESULT) : undefined}
                  bundleGenerationsRemaining={bundleGenerationsRemaining}
                  bundleSize={GENERATIONS_PER_BUNDLE}
                  bundleCost={creditsForStagingTier(selectedStagingTier)}
                  referenceImageUrl={sameRoomAnchorUrl ?? referenceImageUrl}
                  sameRoomMode={!!sameRoomAnchorUrl}
                  onClearReference={() => {
                    if (sameRoomAnchorUrl) {
                      clearSameRoomBatch();
                    } else {
                      setReferenceImageUrl(null);
                    }
                  }}
                  onSetReferenceImage={(dataUrl) => setReferenceImageUrl(dataUrl)}
                  selectedRoomType={selectedRoomType}
                  selectedStyle={selectedFurnitureStyle}
                  selectedStagingTier={selectedStagingTier}
                  onSelectedRoomTypeChange={setSelectedRoomType}
                  onSelectedStyleChange={setSelectedFurnitureStyle}
                  onSelectedStagingTierChange={setSelectedStagingTier}
                  showModelPicker={import.meta.env.DEV || isAdmin}
                />
                {error && (
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-6 py-3 rounded-full shadow-lg font-medium animate-bounce border border-red-400">
                    {error}
                  </div>
                )}
                {/* Same-room batch progress: makes it clear the other uploaded
                    angles are queued (only one angle is edited at a time). */}
                {!error && sameRoomTotal > 1 && (appState === AppState.EDITOR || appState === AppState.PROCESSING) && (
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900/90 backdrop-blur-md border border-purple-500/40 text-slate-100 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                    <Layers className="w-4 h-4 text-purple-300 shrink-0" />
                    <span>
                      Same room · angle <span className="font-semibold">{sameRoomCurrentIndex + 1}</span> of {sameRoomTotal}
                      {angleQueue.length > 0 && (
                        <span className="text-slate-400"> · {angleQueue.length} more queued after this</span>
                      )}
                      {sameRoomInventory && sameRoomCurrentIndex > 0 && (
                        <span className="text-purple-300/80"> · matching {sameRoomInventory.pieces.length} pieces</span>
                      )}
                    </span>
                  </div>
                )}
                {isUploadingOriginal && (
                    <div className="absolute bottom-6 right-6 z-50 bg-slate-800/80 text-slate-300 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-md border border-slate-700">
                        <Loader2 size={12} className="animate-spin" />
                        Saving to cloud...
                    </div>
                )}
              </>
            )
        )}

      </main>

      {/* Footer in scroll flow below main so it doesn't overlap upload panel */}
      {!showDashboard && (
        <div className="shrink-0">
          <Footer />
        </div>
      )}
      </div>

      {/* Result Viewer - rendered as overlay to ensure visibility */}
      {appState === AppState.RESULT && (
        images.processedUrl ? (
          <div className="fixed inset-0 z-40 bg-black">
            <ResultViewer
              originalUrl={images.originalUrl || images.processedUrl}
              processedUrl={images.processedUrl}
              onBack={() => {
                if (autoAdvanceTimerRef.current) {
                  clearTimeout(autoAdvanceTimerRef.current);
                  autoAdvanceTimerRef.current = null;
                }
                setAppState(AppState.EDITOR);
              }}
              onAccept={handleAccept}
              onDownload={handleDownload}
              userId={session?.user?.id}
              onSendToHyperdusk={lastOutputUrl ? () => handleSendToHyperdusk(lastOutputUrl) : undefined}
              stagingImageId={lastStagingImageId}
              onAnimateStaging={lastStagingImageId ? () => handleAnimateStaging(lastStagingImageId) : undefined}
              onSendTo360Tours={!photoplanReturn && images.is360 && resultHandoffImageUrl ? () => handleSendTo360Tours(resultHandoffImageUrl) : undefined}
              onSendToPhotoplan={
                photoplanReturn && (lastOutputUrl || images.processedUrl)
                  ? () => handleSendToPhotoplan(lastOutputUrl || images.processedUrl!)
                  : undefined
              }
              sendToPhotoplanLabel={
                photoplanReturn?.returnPath ? 'Save to Photoplan gallery' : undefined
              }
              onRequestChanges={images.is360 ? handleRequestChanges : undefined}
              aiOperation="virtual_staging"
              aiClassification="AI_MODIFIED"
            />
          </div>
        ) : (
          <div className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center gap-4">
            <p className="text-red-400 text-lg">Error: Result image not found</p>
            <p className="text-slate-400 text-sm">The processed image was not saved correctly.</p>
            <button 
              onClick={() => {
                setAppState(AppState.UPLOAD);
                setShowDashboard(true);
              }}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-500 font-medium"
            >
              Go to Gallery
            </button>
          </div>
        )
      )}

      {/* Full renovation / staging: structure still wrong after auto-retries. */}
      {appState === AppState.RESULT && images.processedUrl && structureWarning && !sameRoomAnchorWarning && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-slate-900/95 backdrop-blur border border-amber-500/50 rounded-2xl shadow-2xl p-4 flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <RefreshCw className="w-5 h-5 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-100 mb-1">Openings or walls look wrong</p>
              <p className="text-xs text-slate-300 leading-relaxed">{structureWarning}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={() => {
                  setStructureWarning(null);
                  setAppState(AppState.EDITOR);
                }}
                className="inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-amber-500 text-slate-900 font-bold text-xs hover:bg-amber-400 active:scale-[0.97] transition-all"
              >
                Try again
              </button>
              <button
                onClick={() => {
                  setStructureWarning(null);
                  handleAccept();
                }}
                className="inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-slate-800 border border-slate-600 text-slate-200 font-semibold text-xs hover:bg-slate-700 active:scale-[0.97] transition-all"
              >
                Fix openings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Same-room batch: the staged hero failed the structural inspection,
          so it was NOT set as the anchor. The user must regenerate before
          staging the other angles. */}
      {appState === AppState.RESULT && images.processedUrl && sameRoomTotal > 1 && sameRoomAnchorWarning && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-slate-900/95 backdrop-blur border border-amber-500/50 rounded-2xl shadow-2xl p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
              <RefreshCw className="w-5 h-5 text-amber-300" />
            </div>
            <p className="text-sm text-slate-200 flex-1">{sameRoomAnchorWarning}</p>
            <button
              onClick={() => {
                setSameRoomAnchorWarning(null);
                setAppState(AppState.EDITOR);
              }}
              className="shrink-0 inline-flex items-center gap-1.5 py-2.5 px-4 rounded-xl bg-amber-500 text-slate-900 font-bold text-sm hover:bg-amber-400 active:scale-[0.97] transition-all"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Same-room batch: prompt the user to stage the next queued angle.
          Requires a structure-clean anchor; after the hero, follow-ups also
          auto-advance shortly and this banner lets them jump ahead. */}
      {appState === AppState.RESULT && images.processedUrl && sameRoomTotal > 1 && angleQueue.length > 0 && !!sameRoomAnchorUrl && !sameRoomAnchorWarning && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-gradient-to-r from-purple-700 to-violet-700 border border-purple-400/50 rounded-2xl shadow-2xl shadow-purple-900/40 p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">
                {angleQueue.length} more {angleQueue.length === 1 ? 'angle' : 'angles'} of this room
              </p>
              <p className="text-xs text-purple-100/80">
                {sameRoomCurrentIndex === 0
                  ? 'Stage the next angle to match this furniture & layout.'
                  : 'Continuing automatically — or jump to the next angle now.'}
              </p>
            </div>
            <button
              onClick={handleStageNextAngle}
              className="shrink-0 inline-flex items-center gap-1.5 py-2.5 px-4 rounded-xl bg-white text-purple-700 font-bold text-sm hover:bg-purple-50 active:scale-[0.97] transition-all"
            >
              Next angle
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Same-room batch finished: all unique angles staged at least once. */}
      {appState === AppState.RESULT && images.processedUrl && sameRoomTotal > 1 && angleQueue.length === 0 && sameRoomCompletedIndices.length >= sameRoomTotal && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="bg-slate-800/95 backdrop-blur border border-emerald-500/40 rounded-2xl shadow-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-emerald-300" />
            </div>
            <p className="text-sm text-slate-200 flex-1">
              All {sameRoomTotal} angles of this room have been staged to match. Find them in your gallery.
            </p>
          </div>
        </div>
      )}

      {sendToTourImageUrl && session?.user?.id && (
        <SendToTourModal
          userId={session.user.id}
          stagedImageUrl={sendToTourImageUrl}
          toursAppUrl="https://360tours.rehub.studio"
          isAdmin={isAdmin}
          onClose={() => setSendToTourImageUrl(null)}
        />
      )}

      {/* One-time "not happy? regenerate" notice on the first staged result.
          AI staging can need a couple of tries; this sets expectations and
          reminds users that each photo includes up to GENERATIONS_PER_BUNDLE
          passes at no extra credit cost. */}
      {showPassesNotice && appState === AppState.RESULT && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-300">
            <button
              onClick={() => setShowPassesNotice(false)}
              className="absolute -top-2 -right-2 z-10 p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700 shadow-2xl">
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-500/20 blur-2xl rounded-full"></div>
                  <div className="relative w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/40 flex items-center justify-center">
                    <RefreshCw className="w-7 h-7 text-purple-300" />
                  </div>
                </div>
              </div>

              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Not quite right? Try again
                </h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  AI staging doesn&apos;t always nail it on the first try. If the result
                  has changed the room or isn&apos;t what you wanted, just hit
                  <span className="text-slate-200 font-medium"> Back to Editor </span>
                  and generate again.
                </p>
              </div>

              <div className="bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
                <Sparkles size={18} className="text-purple-300 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300 leading-relaxed">
                  Each photo includes up to{' '}
                  <span className="font-semibold text-white">{GENERATIONS_PER_BUNDLE} passes</span>.
                  Regenerating this photo won&apos;t cost extra credits until all{' '}
                  {GENERATIONS_PER_BUNDLE} are used.
                </p>
              </div>

              <button
                onClick={() => {
                  try { localStorage.setItem('staging_passes_notice_dismissed', '1'); } catch { /* ignore */ }
                  setShowPassesNotice(false);
                }}
                className="w-full py-3.5 px-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="w-full max-w-lg">
                  <Login 
                    onClose={() => setShowLoginModal(false)} 
                    initialMode={authMode}
                  />
              </div>
          </div>
      )}

      {/* Animated Login Prompt for non-logged-in users who upload */}
      {showLoginPrompt && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
              <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-300">
                  {/* Close button */}
                  <button 
                    onClick={() => { setShowLoginPrompt(false); setPendingFile(null); setPendingQueue([]); }}
                    className="absolute -top-2 -right-2 z-10 p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  >
                    <X size={20} />
                  </button>
                  
                  {/* Card */}
                  <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700 shadow-2xl">
                      {/* Logo */}
                      <div className="flex justify-center mb-6">
                          <div className="relative">
                              <div className="absolute inset-0 bg-purple-500/20 blur-2xl rounded-full animate-pulse"></div>
                              <img 
                                src="/Virtual Staging Studio Logo (500 × 300 Px) - 2.png" 
                                alt="Virtual Staging Studio" 
                                className="relative h-20 w-auto drop-shadow-lg"
                              />
                          </div>
                      </div>
                      
                      {/* Image preview thumbnail */}
                      {pendingFile && (
                          <div className="flex justify-center mb-4">
                              <div className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-slate-600 shadow-lg">
                                  <img 
                                    src={URL.createObjectURL(pendingFile)} 
                                    alt="Uploaded" 
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                  <ImageIcon className="absolute bottom-1 right-1 w-4 h-4 text-white/70" />
                              </div>
                          </div>
                      )}
                      
                      {/* Text */}
                      <div className="text-center mb-8">
                          <h2 className="text-2xl font-bold text-white mb-2">
                              Ready to Stage!
                          </h2>
                          <p className="text-slate-400">
                              Sign in to start virtually staging your room with AI.
                          </p>
                          <p className="text-purple-400 text-sm mt-2 font-medium">
                              ✨ Get 10 free credits when you sign up!
                          </p>
                      </div>
                      
                      {/* Buttons */}
                      <div className="space-y-3">
                          <button
                            onClick={() => handleLoginPromptAuth('signup')}
                            className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <UserPlus size={20} />
                            Create Free Account
                          </button>
                          
                          <button
                            onClick={() => handleLoginPromptAuth('login')}
                            className="w-full py-4 px-6 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <LogIn size={20} />
                            Sign In
                          </button>
                      </div>
                      
                      {/* Fine print */}
                      <p className="text-center text-slate-500 text-xs mt-6">
                          Your image is saved and will be ready after you sign in.
                      </p>
                  </div>
              </div>
          </div>
      )}

      {showDashboard && userProfile && (
          <Dashboard 
             user={userProfile} 
             onClose={() => setShowDashboard(false)} 
             onLogout={handleLogout}
             onOpenEditor={handleImageSelected}
             onConsumeCredit={consumeCredits}
             onContinueEditing={handleContinueEditing}
             onMatchThisImage={(outputUrl) => {
               clearSameRoomBatch();
               setReferenceImageUrl(outputUrl);
               setShowDashboard(false);
               setAppState(AppState.UPLOAD);
             }}
             onStageAnotherAngleOfRoom={handleStageAnotherAngleOfRoom}
             currentApp="staging"
          />
      )}

      {showBuyCreditsModal && userProfile && (
          <BuyCreditsModal user={userProfile} onClose={() => setShowBuyCreditsModal(false)} />
      )}

      {showReferrals && userProfile && (
          <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
      )}

      <ReferralPromoPopup
          client={supabase}
          userId={userProfile?.id}
          appSource="staging"
          onInvite={() => setShowReferrals(true)}
          suppressed={showReferrals || showBuyCreditsModal || showAdminPanel}
      />

      {/* Admin Panel */}
      {showAdminPanel && userProfile && isAdmin && adminRole && (
          <AdminPanel
             user={userProfile}
             adminRole={adminRole}
             onClose={() => setShowAdminPanel(false)}
          />
      )}

      {/* Admin Button - Floating on Dashboard */}
      {showDashboard && userProfile && isAdmin && !showAdminPanel && (
          <button
            onClick={() => setShowAdminPanel(true)}
            className="fixed bottom-20 right-4 z-[9999] bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
            Admin Panel
          </button>
      )}
      
      {/* Version Footer */}
      <div className="fixed bottom-2 right-2 text-[10px] text-slate-600 z-10">
        v{APP_VERSION}
      </div>
      
    </div>
  );
}

export default App;